import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query"
import { useEffect, useRef, useState, type FormEvent, type ReactNode } from "react"
import { Link, useNavigate } from "react-router-dom"
import { api, idempotencyKey, toApiMessage } from "../api/client"
import type { AppNotification, FamilyMember, Paged } from "../api/types"
import { useFamily } from "../family/FamilyContext"
import { canReward } from "../lib/permissions"
import { formatCompact } from "../lib/format"
import { InviteMemberSheet } from "../features/family/InviteMemberSheet"
import { TaskMediaGallery } from "../features/tasks/TaskMediaGallery"
import { optimizeTaskPhoto } from "../features/tasks/optimizeTaskPhoto"
import { sortByClosestDate } from "../features/tasks/taskSorting"
import { useTaskReminder } from "../features/tasks/useTaskReminder"
import {
  Badge,
  Button,
  Dialog,
  EmptyState,
  ErrorState,
  Field,
  FormSection,
  LoadingState,
  PageHeader,
  Segmented,
  StickyActionBar,
  showToast
} from "../components/ui"
import {
  AbilityCard as FamilyAbilityCard,
  AchievementCard as FamilyAchievementCard,
  ActivityItem as FamilyActivityItem,
  ApprovalCard as FamilyApprovalCard,
  AudienceSelector as FamilyAudienceSelector,
  CurrencyAmount,
  CurrencyCard as FamilyCurrencyCard,
  EmojiIcon,
  NotificationItem as FamilyNotificationItem,
  OfferCard as FamilyOfferCard,
  PersonAvatar,
  TaskCard as FamilyTaskCard,
  type TaskUiStatus
} from "../components/family"

type Audience = { memberIds: string[]; groupIds: string[] }
type Group = { id: string; name: string; isSystem: boolean; memberships: Array<{ memberId: string }> }
type Currency = {
  id: string
  name: string
  code: string
  symbol: string
  icon: string
  color: string
  description: string
  transferEnabled: boolean
  redeemProviderApprovalRequired: boolean
  balanceVisibility?: "FAMILY" | "RESTRICTED"
  issueAudience: Audience
  receiveAudience: Audience
  deductAudience: Audience
  redeemProviderAudience: Audience
  balances: Array<{ memberId: string; amount: string }>
}
type CurrencyAction = {
  kind: "issue" | "transfer" | "redeem" | "deduct"
  currency: Currency
  targetMemberId?: string
}
type Assignment = {
  id: string
  memberId: string | null
  status: string
  member?: FamilyMember | null
  completion?: TaskCompletion | null
}
type TaskCompletion = {
  id: string
  comment: string
  checklistState?: Array<{ id: string; checked: boolean }>
  media: Array<{ url: string; type: "IMAGE" | "VIDEO" }>
  submitter?: FamilyMember
  approvals: Array<{ id: string; eligibleMemberId: string; status: string; reason?: string }>
}
type TaskReward = {
  currencyAmount?: string | null
  abilityCount?: number | null
  currency?: { id: string; name: string; icon: string } | null
  ability?: { id: string; name: string; icon: string } | null
  achievement?: { id: string; name: string; icon: string } | null
}
type Occurrence = {
  id: string
  status: string
  dueAt?: string | null
  overdueNotifiedAt?: string | null
  participantSnapshot: string[]
  definition: {
    id: string
    createdByMemberId: string
    title: string
    description: string
    assignmentStrategy: "ANY" | "ALL" | "ROTATION"
    assigneeAudience: Audience
    completionMode: string
    approverAudience?: Audience | null
    approverMode?: "ANY_ONE" | "ALL"
    recurrence?: { frequency?: string; interval?: number } | null
    overduePolicy?: string
    penaltyCurrencyId?: string | null
    penaltyAmount?: string | null
    checklist: Array<{ id: string; title: string }>
    rewards: TaskReward[]
  }
  assignments: Assignment[]
}
type RecurringTask = Occurrence["definition"] & {
  nextOccurrenceAt?: string | null
  recurrence?: { frequency?: string; interval?: number } | null
}
type Ability = {
  id: string
  name: string
  description: string
  icon: string
  color: string
  rarity: string
  targetRequired: boolean
  effect: "MANUAL" | "SKIP_TASK"
  holderAudience: Audience
  instances: Array<{ id: string; status: string; use?: { id: string; status: string } | null }>
}
type Offer = {
  id: string
  title: string
  description: string
  icon: string
  priceAmount: string
  priceCurrency: Currency
  ability: Ability
}
type Achievement = {
  id: string
  name: string
  description: string
  icon: string
  rarity: string
  rewardConfig?: {
    currency?: { currencyId: string; amount: string }
    ability?: { definitionId: string; count: number }
  } | null
  awards: Array<{ id: string; recipient: FamilyMember }>
}
type Activity = {
  id: string
  type: string
  title: string
  body: string
  createdAt: string
  data?: Record<string, unknown>
}
type Actions = {
  approvals: Array<{
    id: string
    completion?: {
      id: string
      comment: string
      checklistState: Array<{ id: string; checked: boolean }>
      media: Array<{ url: string; type: "IMAGE" | "VIDEO" }>
      submitter: FamilyMember
      assignment: { occurrence: { definition: { title: string } } }
    } | null
    abilityUse?: { id: string; actor: FamilyMember; instance: { definition: { name: string } } } | null
  }>
  redeems: Array<{ id: string; amount: string; comment: string; actorMemberId: string; currency: Currency }>
}

function useFamilyId() {
  const { activeFamily } = useFamily()
  if (!activeFamily) throw new Error("Семья не выбрана")
  return { familyId: activeFamily.id, memberId: activeFamily.memberId, role: activeFamily.role }
}
function collection<T>(value: { items: T[] } | undefined) {
  return value?.items ?? []
}
function amount(value: string) {
  return formatCompact(value)
}

function localDateTimeToIso(value: FormDataEntryValue | null) {
  const text = typeof value === "string" ? value.trim() : ""
  return text ? new Date(text).toISOString() : undefined
}

function isoToLocalDateTime(value?: string | null) {
  if (!value) return ""
  const date = new Date(value)
  return new Date(date.valueOf() - date.getTimezoneOffset() * 60_000).toISOString().slice(0, 16)
}

function audienceMembers(audience: Audience, groups: Group[]) {
  return [
    ...new Set([
      ...audience.memberIds,
      ...groups
        .filter((group) => audience.groupIds.includes(group.id))
        .flatMap((group) => group.memberships.map((membership) => membership.memberId))
    ])
  ]
}

function audienceHas(audience: Audience, memberId: string, groups: Group[]) {
  return (
    audience.memberIds.includes(memberId) ||
    groups.some(
      (group) => audience.groupIds.includes(group.id) && group.memberships.some((item) => item.memberId === memberId)
    )
  )
}

function membersInAudience(audience: Audience, groups: Group[]) {
  return [
    ...new Set([
      ...audience.memberIds,
      ...groups
        .filter((group) => audience.groupIds.includes(group.id))
        .flatMap((group) => group.memberships.map((item) => item.memberId))
    ])
  ]
}

function isMyCurrency(currency: Currency, memberId: string, groups: Group[]) {
  const ownBalance = BigInt(currency.balances.find((balance) => balance.memberId === memberId)?.amount ?? 0)
  return ownBalance !== 0n || audienceHas(currency.receiveAudience, memberId, groups)
}

export function HomeV1Page() {
  const { familyId, memberId } = useFamilyId()
  const groups = useGroups()
  const [selectedTask, setSelectedTask] = useState<Occurrence | null>(null)
  const tasks = useQuery({
    queryKey: ["v1", familyId, "tasks", "today"],
    queryFn: () => api.get<{ items: Occurrence[] }>(`/families/${familyId}/tasks?view=today`),
    refetchInterval: 5_000
  })
  const currencies = useQuery({
    queryKey: ["v1", familyId, "currencies"],
    queryFn: () => api.get<{ items: Currency[] }>(`/families/${familyId}/currencies`)
  })
  const activity = useQuery({
    queryKey: ["v1", familyId, "activity"],
    queryFn: () => api.get<{ items: Activity[] }>(`/families/${familyId}/activity`)
  })
  const activeTasks = sortByClosestDate(collection(tasks.data), (item) => item.dueAt).filter(
    (item) => item.status !== "COMPLETED"
  )
  const doingTasks = activeTasks.filter(
    (item) =>
      item.assignments.some((assignment) => assignment.memberId === memberId) ||
      (item.definition.assignmentStrategy === "ANY" && item.participantSnapshot.includes(memberId))
  )
  const createdTasks = activeTasks.filter((item) => item.definition.createdByMemberId === memberId)
  const myCurrencies = collection(currencies.data).filter((currency) => isMyCurrency(currency, memberId, groups))
  return (
    <div className="page-stack">
      <PageHeader title="Главная" description="Сегодня в вашей семье" />
      <section className="v1-hero">
        <div>
          <span className="v1-hero__emoji">🏡</span>
          <h2>
            {doingTasks.length
              ? `${doingTasks.length} ${doingTasks.length === 1 ? "задача" : "задачи"} на сегодня`
              : "Сегодня всё спокойно"}
          </h2>
          <p>{doingTasks[0]?.definition.title ?? "Можно придумать что-нибудь приятное вместе"}</p>
        </div>
        <div className="v1-balances">
          {myCurrencies.slice(0, 3).map((currency) => (
            <div key={currency.id}>
              <EmojiIcon value={currency.icon} />
              <strong>
                {amount(currency.balances.find((balance) => balance.memberId === memberId)?.amount ?? "0")}
              </strong>
              <small>{currency.name}</small>
            </div>
          ))}
        </div>
      </section>
      <div className="v1-grid">
        <section className="panel">
          <div className="section-heading">
            <h2>Ближайшие задачи</h2>
            <Link to="/tasks">Все задачи</Link>
          </div>
          {tasks.isLoading ? (
            <LoadingState />
          ) : doingTasks.length || createdTasks.length ? (
            <div className="home-task-groups">
              {doingTasks.length ? (
                <section className="home-task-group">
                  <div className="section-heading section-heading--compact">
                    <h3>Делаю</h3>
                    <Badge tone="violet">{doingTasks.length}</Badge>
                  </div>
                  {doingTasks.slice(0, 4).map((item) => (
                    <TaskCard key={`doing:${item.id}`} item={item} onOpen={() => setSelectedTask(item)} />
                  ))}
                </section>
              ) : null}
              {createdTasks.length ? (
                <section className="home-task-group">
                  <div className="section-heading section-heading--compact">
                    <h3>Поставил(а)</h3>
                    <Badge tone="neutral">{createdTasks.length}</Badge>
                  </div>
                  {createdTasks.slice(0, 4).map((item) => (
                    <TaskCard key={`created:${item.id}`} item={item} onOpen={() => setSelectedTask(item)} />
                  ))}
                </section>
              ) : null}
            </div>
          ) : (
            <EmptyState icon="☀️" title="Задач нет" text="Свободное время тоже важно" />
          )}
        </section>
        <section className="panel">
          <h2>Что происходило</h2>
          {collection(activity.data)
            .slice(0, 6)
            .map((item) => (
              <ActivityRow key={item.id} item={item} />
            ))}
          {!collection(activity.data).length ? (
            <EmptyState icon="✨" title="Здесь появятся события" text="Начисления, задачи и семейные достижения" />
          ) : null}
        </section>
      </div>
      <TaskDetailsDialog item={selectedTask} onClose={() => setSelectedTask(null)} />
    </div>
  )
}

export function TasksV1Page() {
  const { familyId, memberId, role } = useFamilyId()
  const navigate = useNavigate()
  const [view, setView] = useState<"today" | "plan" | "recurring">("today")
  const [complete, setComplete] = useState<{ assignment: Assignment; item: Occurrence } | null>(null)
  const [selected, setSelected] = useState<Occurrence | null>(null)
  const [review, setReview] = useState<{ completion: TaskCompletion; title: string } | null>(null)
  const reminder = useTaskReminder(familyId)
  const queryClient = useQueryClient()
  const query = useQuery({
    queryKey: ["v1", familyId, "tasks", view],
    queryFn: () => api.get<{ items: Occurrence[] }>(`/families/${familyId}/tasks?view=${view}`),
    enabled: view !== "recurring",
    refetchInterval: 5_000
  })
  const recurring = useQuery({
    queryKey: ["v1", familyId, "tasks", "recurring"],
    queryFn: () => api.get<{ items: RecurringTask[] }>(`/families/${familyId}/tasks?view=recurring`),
    enabled: view === "recurring",
    refetchInterval: 5_000
  })
  const refresh = () => queryClient.invalidateQueries({ queryKey: ["v1", familyId] })
  const claim = useMutation({
    mutationFn: (id: string) => api.post(`/families/${familyId}/tasks/occurrences/${id}/claim`),
    onSuccess: () => {
      showToast("Задача теперь ваша")
      void refresh()
    },
    onError: (e) => showToast(toApiMessage(e), "danger")
  })
  const decide = useMutation({
    mutationFn: (input: { completionId: string; approve: boolean; reason?: string }) =>
      api.post(`/families/${familyId}/tasks/completions/${input.completionId}/decision`, {
        approve: input.approve,
        reason: input.reason
      }),
    onSuccess: () => {
      showToast("Решение сохранено")
      setReview(null)
      void refresh()
    },
    onError: (error) => showToast(toApiMessage(error), "danger")
  })
  const withdraw = useMutation({
    mutationFn: (completionId: string) => api.post(`/families/${familyId}/tasks/completions/${completionId}/withdraw`),
    onSuccess: () => {
      showToast("Отчёт отозван — его можно изменить и отправить снова")
      void refresh()
    },
    onError: (error) => showToast(toApiMessage(error), "danger")
  })
  async function archiveRecurring(task: RecurringTask) {
    if (!window.confirm(`Удалить «${task.title}» из расписания? Уже созданные задачи останутся в истории.`)) return
    try {
      await api.delete(`/families/${familyId}/tasks/definitions/${task.id}`)
      showToast("Повторение удалено")
      await refresh()
    } catch (error) {
      showToast(toApiMessage(error), "danger")
    }
  }
  async function runRecurringNow(task: RecurringTask) {
    try {
      await api.post(`/families/${familyId}/tasks/definitions/${task.id}/run-now`)
      showToast("Задача поставлена сейчас")
      await refresh()
    } catch (error) {
      showToast(toApiMessage(error), "danger")
    }
  }
  return (
    <div className="page-stack">
      <PageHeader
        title="Задачи"
        actions={
          <Button icon="plus" onClick={() => navigate("/tasks/new")}>
            Новая задача
          </Button>
        }
      />
      <Segmented
        value={view}
        onChange={(value) => setView(value as typeof view)}
        options={[
          { value: "today", label: "Сегодня" },
          { value: "plan", label: "План" },
          { value: "recurring", label: "Повторяются" }
        ]}
      />
      {view === "recurring" ? (
        recurring.isLoading ? (
          <LoadingState rows={5} />
        ) : recurring.error ? (
          <ErrorState retry={() => void recurring.refetch()} />
        ) : collection(recurring.data).length ? (
          <div className="task-list">
            {sortByClosestDate(collection(recurring.data), (task) => task.nextOccurrenceAt).map((task) => (
              <article className="task-card" key={task.id}>
                <div className="task-card__state">↻</div>
                <div>
                  <h3>{task.title}</h3>
                  {task.description ? <p>{task.description}</p> : null}
                  <div className="task-card__meta">
                    <Badge tone="violet">Повторяется</Badge>
                    <span>
                      Следующий запуск:{" "}
                      {task.nextOccurrenceAt
                        ? new Date(task.nextOccurrenceAt).toLocaleString("ru-RU")
                        : "не запланирован"}
                    </span>
                  </div>
                </div>
                {task.createdByMemberId === memberId || role === "OWNER" || role === "ADMIN" ? (
                  <div className="task-card__actions">
                    {task.createdByMemberId === memberId ? (
                      <Button size="sm" variant="ghost" onClick={() => navigate(`/tasks/recurring/${task.id}/edit`)}>
                        Редактировать
                      </Button>
                    ) : null}
                    <Button size="sm" onClick={() => void runRecurringNow(task)}>
                      Поставить сейчас
                    </Button>
                    <Button size="sm" variant="danger" onClick={() => void archiveRecurring(task)}>
                      Удалить
                    </Button>
                  </div>
                ) : null}
              </article>
            ))}
          </div>
        ) : (
          <EmptyState
            icon="↻"
            title="Нет повторяющихся задач"
            text="Для задачи можно настроить ежедневный, недельный или месячный повтор"
          />
        )
      ) : query.isLoading ? (
        <LoadingState rows={5} />
      ) : query.error ? (
        <ErrorState retry={() => void query.refetch()} />
      ) : collection(query.data).length ? (
        <TaskStatusLists
          items={collection(query.data)}
          memberId={memberId}
          onOpen={setSelected}
          onEdit={(item) => navigate(`/tasks/${item.id}/edit`)}
          onClaim={(item) => claim.mutate(item.id)}
          onComplete={(assignment, item) => setComplete({ assignment, item })}
          onReview={(completion, item) => setReview({ completion, title: item.definition.title })}
          onApprove={(completion) => decide.mutate({ completionId: completion.id, approve: true })}
          onWithdraw={(completion) => withdraw.mutate(completion.id)}
          onRemind={(item) => void reminder.remind(item.id)}
          reminderPendingId={reminder.pendingId}
          reminderCoolingDown={reminder.coolingDown}
        />
      ) : (
        <EmptyState
          icon="✅"
          title="Задач пока нет"
          text="Создайте первую задачу — разовую или повторяющуюся"
          action={<Button onClick={() => navigate("/tasks/new")}>Создать задачу</Button>}
        />
      )}
      <CompletionDialog
        assignment={complete}
        onClose={() => setComplete(null)}
        onDone={() => {
          setComplete(null)
          void refresh()
        }}
      />
      <TaskDetailsDialog item={selected} onClose={() => setSelected(null)} />
      <TaskReviewDialog
        completion={review?.completion ?? null}
        title={review?.title ?? "Результат задачи"}
        loading={decide.isPending}
        onClose={() => setReview(null)}
        onDecision={(approve, reason) => {
          if (review) decide.mutate({ completionId: review.completion.id, approve, reason })
        }}
      />
    </div>
  )
}

function TaskStatusLists({
  items,
  memberId,
  onOpen,
  onEdit,
  onClaim,
  onComplete,
  onReview,
  onApprove,
  onWithdraw,
  onRemind,
  reminderPendingId,
  reminderCoolingDown
}: {
  items: Occurrence[]
  memberId: string
  onOpen(item: Occurrence): void
  onEdit(item: Occurrence): void
  onClaim(item: Occurrence): void
  onComplete(assignment: Assignment, item: Occurrence): void
  onReview(completion: TaskCompletion, item: Occurrence): void
  onApprove(completion: TaskCompletion): void
  onWithdraw(completion: TaskCompletion): void
  onRemind(item: Occurrence): void
  reminderPendingId: string | null
  reminderCoolingDown: boolean
}) {
  const sorted = sortByClosestDate(items, (item) => item.dueAt)
  const sections = [
    { title: "Нужно сделать", items: sorted.filter((item) => ["OPEN", "REJECTED"].includes(item.status)) },
    { title: "На проверке", items: sorted.filter((item) => item.status === "SUBMITTED") },
    { title: "Завершённые", items: sorted.filter((item) => item.status === "COMPLETED") }
  ]
  return (
    <div className="task-sections">
      {sections.map((section) => (
        <section key={section.title} className="task-section">
          <div className="section-heading">
            <h2>{section.title}</h2>
            <Badge>{section.items.length}</Badge>
          </div>
          {section.items.length ? (
            <div className="task-list">
              {section.items.map((item) => (
                <TaskCard
                  key={item.id}
                  item={item}
                  onOpen={() => onOpen(item)}
                  actions={
                    <>
                      {reminderTargets(item, memberId).length ? (
                        <Button
                          size="sm"
                          variant="soft"
                          icon="bell"
                          loading={reminderPendingId === item.id}
                          disabled={reminderCoolingDown}
                          title={reminderCoolingDown ? "Следующее напоминание можно отправить через 5 секунд" : undefined}
                          onClick={() => onRemind(item)}
                        >
                          Напомнить
                        </Button>
                      ) : null}
                      {item.definition.createdByMemberId === memberId && ["OPEN", "REJECTED"].includes(item.status) ? (
                        <Button size="sm" variant="ghost" onClick={() => onEdit(item)}>
                          Редактировать
                        </Button>
                      ) : null}
                      {item.definition.assignmentStrategy === "ANY" &&
                      item.participantSnapshot.length > 1 &&
                      item.participantSnapshot.includes(memberId) &&
                      item.assignments.some((assignment) => !assignment.memberId) ? (
                        <Button size="sm" onClick={() => onClaim(item)}>
                          Взять себе
                        </Button>
                      ) : null}
                      {item.assignments
                        .filter(
                          (assignment) =>
                            assignment.memberId === memberId &&
                            ["OPEN", "CLAIMED", "REJECTED"].includes(assignment.status)
                        )
                        .map((assignment) => (
                          <Button key={assignment.id} size="sm" onClick={() => onComplete(assignment, item)}>
                            {assignment.status === "REJECTED" ? "Исправить отчёт" : "Отправить результат"}
                          </Button>
                        ))}
                      {item.assignments
                        .filter(
                          (assignment) =>
                            assignment.memberId === memberId &&
                            assignment.status === "SUBMITTED" &&
                            Boolean(assignment.completion)
                        )
                        .map((assignment) => (
                          <span className="button-row" key={`submitted:${assignment.id}`}>
                            <Button size="sm" variant="ghost" onClick={() => onComplete(assignment, item)}>
                              Редактировать результат
                            </Button>
                            <Button size="sm" variant="ghost" onClick={() => onWithdraw(assignment.completion!)}>
                              Отозвать
                            </Button>
                          </span>
                        ))}
                      {item.assignments
                        .flatMap((assignment) => (assignment.completion ? [assignment.completion] : []))
                        .filter((completion) =>
                          completion.approvals.some(
                            (approval) => approval.eligibleMemberId === memberId && approval.status === "PENDING"
                          )
                        )
                        .map((completion) => (
                          <span className="button-row" key={`review:${completion.id}`}>
                            <Button size="sm" variant="ghost" onClick={() => onReview(completion, item)}>
                              Проверить / отклонить
                            </Button>
                            <Button size="sm" onClick={() => onApprove(completion)}>
                              Принять
                            </Button>
                          </span>
                        ))}
                    </>
                  }
                />
              ))}
            </div>
          ) : (
            <p className="muted">Пусто</p>
          )}
        </section>
      ))}
    </div>
  )
}

function TaskCard({ item, actions, onOpen }: { item: Occurrence; actions?: ReactNode; onOpen?(): void }) {
  const isOverdue = Boolean(item.overdueNotifiedAt) && ["OPEN", "REJECTED"].includes(item.status)
  const status: TaskUiStatus =
    item.status === "COMPLETED" ||
    item.status === "MISSED" ||
    item.status === "SKIPPED" ||
    item.status === "SUBMITTED" ||
    item.status === "REJECTED"
      ? item.status
      : isOverdue
        ? "OVERDUE"
        : item.assignments.some((assignment) => !assignment.memberId)
          ? "CLAIMABLE"
          : "CLAIMED"
  const reward = item.definition.rewards[0]
  const rewardLabel = reward?.currency
    ? `${reward.currencyAmount ?? ""} ${reward.currency.icon}`
    : reward?.ability
      ? `${reward.ability.icon} ${reward.ability.name}${(reward.abilityCount ?? 1) > 1 ? ` ×${reward.abilityCount}` : ""}`
      : reward?.achievement
        ? `${reward.achievement.icon} ${reward.achievement.name}`
        : undefined
  return (
    <FamilyTaskCard
      title={item.definition.title}
      description={item.definition.description}
      status={status}
      dueLabel={
        item.dueAt
          ? new Date(item.dueAt).toLocaleString("ru-RU", {
              day: "numeric",
              month: "short",
              hour: "2-digit",
              minute: "2-digit"
            })
          : undefined
      }
      people={item.assignments.flatMap((assignment) =>
        assignment.member ? [{ id: assignment.member.id, displayName: assignment.member.displayName }] : []
      )}
      reward={rewardLabel}
      actions={
        <>
          {onOpen ? (
            <Button size="sm" variant="ghost" onClick={onOpen}>
              Открыть
            </Button>
          ) : null}
          {actions}
        </>
      }
    />
  )
}

function reminderTargets(item: Occurrence, actorMemberId: string) {
  if (!["OPEN", "REJECTED", "SUBMITTED"].includes(item.status)) return []
  const targetIds =
    item.status === "SUBMITTED"
      ? item.assignments.flatMap((assignment) =>
          assignment.completion?.approvals
            .filter((approval) => approval.status === "PENDING")
            .map((approval) => approval.eligibleMemberId) ?? []
        )
      : item.assignments.flatMap((assignment) => (assignment.memberId ? [assignment.memberId] : []))
  return [...new Set(targetIds)].filter((id) => id !== actorMemberId)
}

function TaskDetailsDialog({ item, onClose }: { item: Occurrence | null; onClose(): void }) {
  if (!item) return null
  const rewards = item.definition.rewards
  const reports = item.assignments.flatMap((assignment) =>
    assignment.completion ? [{ assignment, completion: assignment.completion }] : []
  )
  return (
    <Dialog open onClose={onClose} title={item.definition.title} size="lg">
      <div className="form-stack task-details">
        {item.definition.description ? <p>{item.definition.description}</p> : <p className="muted">Без описания</p>}
        <div className="detail-grid">
          <div>
            <small>Срок</small>
            <strong>{item.dueAt ? new Date(item.dueAt).toLocaleString("ru-RU") : "Без срока"}</strong>
          </div>
          <div>
            <small>Исполнители</small>
            <strong>
              {item.assignments
                .map((assignment) => assignment.member?.displayName)
                .filter(Boolean)
                .join(", ") || "Задачу можно взять"}
            </strong>
          </div>
          <div>
            <small>Приёмка</small>
            <strong>
              {item.definition.completionMode === "REQUIRE_APPROVAL" ? "После проверки" : "Автоматически"}
            </strong>
          </div>
        </div>
        {item.definition.checklist.length ? (
          <section>
            <h3>Что нужно сделать</h3>
            <ul className="plain-list">
              {item.definition.checklist.map((entry) => (
                <li key={entry.id}>✓ {entry.title}</li>
              ))}
            </ul>
          </section>
        ) : null}
        {rewards.length ? (
          <section>
            <h3>Награда после выполнения</h3>
            <div className="chip-row">
              {rewards.map((reward, index) => (
                <Badge key={index} tone="violet">
                  {reward.currency
                    ? `${reward.currency.icon} ${reward.currencyAmount} ${reward.currency.name}`
                    : reward.ability
                      ? `${reward.ability.icon} ${reward.ability.name} ×${reward.abilityCount ?? 1}`
                      : `${reward.achievement?.icon ?? "🏆"} ${reward.achievement?.name ?? "Достижение"}`}
                </Badge>
              ))}
            </div>
          </section>
        ) : null}
        {reports.length ? (
          <section>
            <h3>{reports.length === 1 ? "Отчёт о выполнении" : "Отчёты о выполнении"}</h3>
            <div className="form-stack form-stack--compact">
              {reports.map(({ assignment, completion }) => (
                <article className="task-report" key={completion.id}>
                  <div>
                    <small>Исполнитель</small>
                    <strong>{assignment.member?.displayName ?? completion.submitter?.displayName ?? "Исполнитель"}</strong>
                  </div>
                  <div>
                    <small>Комментарий</small>
                    <p>{completion.comment || "Без комментария"}</p>
                  </div>
                  <div>
                    <small>Приложенные файлы</small>
                    <TaskMediaGallery media={completion.media} emptyMessage="Файлы не приложены" />
                  </div>
                </article>
              ))}
            </div>
          </section>
        ) : null}
        <div className="dialog__actions">
          <Button type="button" onClick={onClose}>
            Закрыть
          </Button>
        </div>
      </div>
    </Dialog>
  )
}

// eslint-disable-next-line @typescript-eslint/no-unused-vars
function LegacyTaskEditDialog({ item, onClose, onDone }: { item: Occurrence | null; onClose(): void; onDone(): void }) {
  const { familyId } = useFamilyId()
  const groups = useGroups()
  const [assignees, setAssignees] = useState<Audience>(
    item?.definition.assigneeAudience ?? { memberIds: [], groupIds: [] }
  )
  const [approvers, setApprovers] = useState<Audience>(
    item?.definition.approverAudience ?? { memberIds: [], groupIds: [] }
  )
  const [strategy, setStrategy] = useState<"ANY" | "ALL" | "ROTATION">(item?.definition.assignmentStrategy ?? "ALL")
  const [recurrence, setRecurrence] = useState(item?.definition.recurrence?.frequency ?? "NONE")
  const [approvalRequired, setApprovalRequired] = useState(item?.definition.completionMode === "REQUIRE_APPROVAL")
  const currentCurrencyReward = item?.definition.rewards.find((reward) => reward.currency)
  const currentAbilityReward = item?.definition.rewards.find((reward) => reward.ability)
  const currentAchievementReward = item?.definition.rewards.find((reward) => reward.achievement)
  const [rewardCurrencyId, setRewardCurrencyId] = useState(currentCurrencyReward?.currency?.id ?? "")
  const [rewardAbilityId, setRewardAbilityId] = useState(currentAbilityReward?.ability?.id ?? "")
  const [rewardAchievementId, setRewardAchievementId] = useState(currentAchievementReward?.achievement?.id ?? "")
  const [penaltyCurrencyId, setPenaltyCurrencyId] = useState(item?.definition.penaltyCurrencyId ?? "")
  const [loading, setLoading] = useState(false)
  const assigneeCount = audienceMembers(assignees, groups).length
  const canClaimOne = assignees.groupIds.length > 0 && assigneeCount > 1
  const effectiveStrategy = assigneeCount <= 1 || (!canClaimOne && strategy === "ANY") ? ("ALL" as const) : strategy
  const currencies = useQuery({
    queryKey: ["v1", familyId, "currencies"],
    queryFn: () => api.get<{ items: Currency[] }>(`/families/${familyId}/currencies`)
  })
  const abilities = useQuery({
    queryKey: ["v1", familyId, "abilities"],
    queryFn: () => api.get<{ items: Ability[] }>(`/families/${familyId}/abilities`)
  })
  const achievements = useQuery({
    queryKey: ["v1", familyId, "achievements"],
    queryFn: () => api.get<{ items: Achievement[] }>(`/families/${familyId}/achievements`)
  })
  async function submit(event: FormEvent<HTMLFormElement>) {
    event.preventDefault()
    if (!item) return
    const data = new FormData(event.currentTarget)
    const rewards: Array<Record<string, unknown>> = []
    if (rewardCurrencyId && data.get("rewardCurrencyAmount"))
      rewards.push({ currencyId: rewardCurrencyId, currencyAmount: data.get("rewardCurrencyAmount") })
    if (rewardAbilityId)
      rewards.push({
        abilityDefinitionId: rewardAbilityId,
        abilityCount: Number(data.get("rewardAbilityCount") || 1)
      })
    if (rewardAchievementId) rewards.push({ achievementDefinitionId: rewardAchievementId })
    setLoading(true)
    try {
      await api.patch(`/families/${familyId}/tasks/definitions/${item.definition.id}`, {
        occurrenceId: item.status === "OPEN" ? item.id : undefined,
        title: data.get("title"),
        description: data.get("description"),
        dueAt: localDateTimeToIso(data.get("dueAt")) ?? null,
        ...(item.status === "OPEN" ? { assigneeAudience: assignees, assignmentStrategy: effectiveStrategy } : {}),
        recurrence:
          recurrence === "NONE"
            ? null
            : { frequency: recurrence, interval: Number(data.get("recurrenceInterval") || 1) },
        completionMode: approvalRequired ? "REQUIRE_APPROVAL" : "TRUST",
        approverAudience: approvalRequired ? approvers : null,
        approverMode: data.get("approverMode") || "ANY_ONE",
        overduePolicy: data.get("overduePolicy"),
        rewards,
        penalty:
          penaltyCurrencyId && data.get("penaltyAmount")
            ? { currencyId: penaltyCurrencyId, amount: data.get("penaltyAmount") }
            : null,
        checklist: String(data.get("checklist") || "")
          .split("\n")
          .map((value) => value.trim())
          .filter(Boolean)
      })
      showToast("Задача обновлена")
      onDone()
    } catch (error) {
      showToast(toApiMessage(error), "danger")
    } finally {
      setLoading(false)
    }
  }
  return (
    <Dialog open={Boolean(item)} onClose={onClose} title="Редактировать задачу" size="lg">
      {item ? (
        <form className="form-stack" onSubmit={submit} key={item.id}>
          <Field label="Название">
            <input name="title" required defaultValue={item.definition.title} maxLength={120} autoFocus />
          </Field>
          <Field label="Описание">
            <textarea name="description" rows={3} defaultValue={item.definition.description} />
          </Field>
          {item.status === "OPEN" ? (
            <>
              <AudienceSelector label="Кому назначена" value={assignees} onChange={setAssignees} />
              {assigneeCount > 1 ? (
                <Field label="Как распределить">
                  <select
                    value={effectiveStrategy}
                    onChange={(event) => setStrategy(event.target.value as typeof strategy)}
                  >
                    {canClaimOne ? <option value="ANY">Кто-нибудь один возьмёт задачу</option> : null}
                    <option value="ALL">Каждый участник</option>
                    <option value="ROTATION">По очереди</option>
                  </select>
                </Field>
              ) : null}
            </>
          ) : (
            <p className="muted">Исполнителей нельзя менять после отправки результата.</p>
          )}
          <Field label="Сделать до">
            <input name="dueAt" type="datetime-local" defaultValue={isoToLocalDateTime(item.dueAt)} />
          </Field>
          <div className="form-row form-row--2">
            <Field label="Повтор">
              <select value={recurrence} onChange={(event) => setRecurrence(event.target.value)}>
                <option value="NONE">Не повторять</option>
                <option value="DAILY">Каждый день</option>
                <option value="WEEKLY">Каждую неделю</option>
                <option value="MONTHLY">Каждый месяц</option>
              </select>
            </Field>
            {recurrence !== "NONE" ? (
              <Field label="Интервал">
                <input
                  name="recurrenceInterval"
                  type="number"
                  min="1"
                  max="365"
                  defaultValue={item.definition.recurrence?.interval ?? 1}
                />
              </Field>
            ) : null}
          </div>
          <Field label="Чек-лист" hint="Один пункт на строку">
            <textarea
              name="checklist"
              rows={4}
              defaultValue={item.definition.checklist.map((entry) => entry.title).join("\n")}
            />
          </Field>
          <label className="check-row">
            <input
              type="checkbox"
              checked={approvalRequired}
              onChange={(event) => setApprovalRequired(event.target.checked)}
            />
            <span>Нужно подтверждение выполнения</span>
          </label>
          {approvalRequired ? (
            <>
              <AudienceSelector label="Кто подтверждает" value={approvers} onChange={setApprovers} />
              <Field label="Сколько подтверждений нужно">
                <select name="approverMode" defaultValue={item.definition.approverMode ?? "ANY_ONE"}>
                  <option value="ANY_ONE">Одного достаточно</option>
                  <option value="ALL">Нужны все</option>
                </select>
              </Field>
            </>
          ) : null}
          <Field label="Если срок прошёл">
            <select name="overduePolicy" defaultValue={item.definition.overduePolicy ?? "KEEP_OVERDUE"}>
              <option value="KEEP_OVERDUE">Оставить просроченной</option>
              <option value="MARK_MISSED_AND_CONTINUE">Отметить пропущенной и продолжить</option>
              <option value="BLOCK_NEXT_UNTIL_CLOSED">Не создавать следующую</option>
            </select>
          </Field>
          <div className="form-row form-row--2">
            <Field label="Награда валютой">
              <select value={rewardCurrencyId} onChange={(event) => setRewardCurrencyId(event.target.value)}>
                <option value="">Без валюты</option>
                {collection(currencies.data).map((currency) => (
                  <option value={currency.id} key={currency.id}>
                    {currency.icon} {currency.name}
                  </option>
                ))}
              </select>
            </Field>
            {rewardCurrencyId ? (
              <Field label="Количество валюты">
                <input
                  name="rewardCurrencyAmount"
                  inputMode="numeric"
                  required
                  defaultValue={currentCurrencyReward?.currencyAmount ?? ""}
                />
              </Field>
            ) : null}
          </div>
          <div className="form-row form-row--2">
            <Field label="Награда способностью">
              <select value={rewardAbilityId} onChange={(event) => setRewardAbilityId(event.target.value)}>
                <option value="">Без способности</option>
                {collection(abilities.data).map((ability) => (
                  <option value={ability.id} key={ability.id}>
                    {ability.icon} {ability.name}
                  </option>
                ))}
              </select>
            </Field>
            {rewardAbilityId ? (
              <Field label="Количество способностей">
                <input
                  name="rewardAbilityCount"
                  type="number"
                  min="1"
                  max="100"
                  required
                  defaultValue={currentAbilityReward?.abilityCount ?? 1}
                />
              </Field>
            ) : null}
          </div>
          <Field label="Награда достижением">
            <select value={rewardAchievementId} onChange={(event) => setRewardAchievementId(event.target.value)}>
              <option value="">Без достижения</option>
              {collection(achievements.data).map((achievement) => (
                <option value={achievement.id} key={achievement.id}>
                  {achievement.icon} {achievement.name}
                </option>
              ))}
            </select>
          </Field>
          <div className="form-row form-row--2">
            <Field label="Штраф за пропуск">
              <select value={penaltyCurrencyId} onChange={(event) => setPenaltyCurrencyId(event.target.value)}>
                <option value="">Без штрафа</option>
                {collection(currencies.data).map((currency) => (
                  <option value={currency.id} key={currency.id}>
                    {currency.icon} {currency.name}
                  </option>
                ))}
              </select>
            </Field>
            {penaltyCurrencyId ? (
              <Field label="Размер штрафа">
                <input
                  name="penaltyAmount"
                  inputMode="numeric"
                  required
                  defaultValue={item.definition.penaltyAmount ?? ""}
                />
              </Field>
            ) : null}
          </div>
          <div className="dialog__actions">
            <Button type="button" variant="ghost" onClick={onClose}>
              Отмена
            </Button>
            <Button type="submit" loading={loading}>
              Сохранить
            </Button>
          </div>
        </form>
      ) : null}
    </Dialog>
  )
}

// eslint-disable-next-line @typescript-eslint/no-unused-vars
function LegacyRecurringTaskEditDialog({
  task,
  onClose,
  onDone
}: {
  task: RecurringTask | null
  onClose(): void
  onDone(): void
}) {
  const { familyId } = useFamilyId()
  const [assignees, setAssignees] = useState<Audience>(task?.assigneeAudience ?? { memberIds: [], groupIds: [] })
  const [loading, setLoading] = useState(false)
  async function submit(event: FormEvent<HTMLFormElement>) {
    event.preventDefault()
    if (!task) return
    const data = new FormData(event.currentTarget)
    setLoading(true)
    try {
      await api.patch(`/families/${familyId}/tasks/definitions/${task.id}`, {
        title: data.get("title"),
        description: data.get("description"),
        checklist: String(data.get("checklist") || "")
          .split("\n")
          .map((value) => value.trim())
          .filter(Boolean),
        assigneeAudience: assignees,
        recurrence: {
          frequency: data.get("frequency"),
          interval: Number(data.get("interval") || 1)
        }
      })
      showToast("Расписание обновлено")
      onDone()
    } catch (error) {
      showToast(toApiMessage(error), "danger")
    } finally {
      setLoading(false)
    }
  }
  return (
    <Dialog open={Boolean(task)} onClose={onClose} title="Редактировать повторение">
      {task ? (
        <form className="form-stack" onSubmit={submit} key={task.id}>
          <Field label="Название">
            <input name="title" required defaultValue={task.title} maxLength={120} autoFocus />
          </Field>
          <Field label="Описание">
            <textarea name="description" rows={3} defaultValue={task.description} />
          </Field>
          <AudienceSelector label="Кому назначать" value={assignees} onChange={setAssignees} />
          <div className="form-row form-row--2">
            <Field label="Повторять">
              <select name="frequency" defaultValue={task.recurrence?.frequency ?? "DAILY"}>
                <option value="DAILY">Каждый день</option>
                <option value="WEEKLY">Каждую неделю</option>
                <option value="MONTHLY">Каждый месяц</option>
              </select>
            </Field>
            <Field label="Интервал" hint="Например, 2 — раз в два дня/недели/месяца">
              <input name="interval" type="number" min="1" max="365" defaultValue={task.recurrence?.interval ?? 1} />
            </Field>
          </div>
          <Field label="Чек-лист" hint="Один пункт на строку">
            <textarea name="checklist" rows={4} defaultValue={task.checklist.map((entry) => entry.title).join("\n")} />
          </Field>
          <div className="dialog__actions">
            <Button type="button" variant="ghost" onClick={onClose}>
              Отмена
            </Button>
            <Button type="submit" loading={loading}>
              Сохранить
            </Button>
          </div>
        </form>
      ) : null}
    </Dialog>
  )
}

export function LegacyTaskCreatePage() {
  const { familyId, memberId } = useFamilyId()
  const navigate = useNavigate()
  const [advanced, setAdvanced] = useState(false)
  const [approvalRequired, setApprovalRequired] = useState(false)
  const [assignees, setAssignees] = useState<Audience>({ memberIds: [], groupIds: [] })
  const [approvers, setApprovers] = useState<Audience>({ memberIds: [memberId], groupIds: [] })
  const [strategy, setStrategy] = useState<"ANY" | "ALL" | "ROTATION">("ALL")
  const [recurrence, setRecurrence] = useState("NONE")
  const [rewardCurrencyId, setRewardCurrencyId] = useState("")
  const [rewardAbilityId, setRewardAbilityId] = useState("")
  const [penaltyCurrencyId, setPenaltyCurrencyId] = useState("")
  const [loading, setLoading] = useState(false)
  const groups = useGroups()
  const assigneeCount = audienceMembers(assignees, groups).length
  const canClaimOne = assignees.groupIds.length > 0 && assigneeCount > 1
  const effectiveStrategy = assigneeCount <= 1 || (!canClaimOne && strategy === "ANY") ? ("ALL" as const) : strategy
  const currencies = useQuery({
    queryKey: ["v1", familyId, "currencies"],
    queryFn: () => api.get<{ items: Currency[] }>(`/families/${familyId}/currencies`)
  })
  const abilities = useQuery({
    queryKey: ["v1", familyId, "abilities"],
    queryFn: () => api.get<{ items: Ability[] }>(`/families/${familyId}/abilities`)
  })
  const achievements = useQuery({
    queryKey: ["v1", familyId, "achievements"],
    queryFn: () => api.get<{ items: Achievement[] }>(`/families/${familyId}/achievements`)
  })
  async function submit(event: FormEvent<HTMLFormElement>) {
    event.preventDefault()
    const data = new FormData(event.currentTarget)
    const rewards: Array<Record<string, unknown>> = []
    if (data.get("rewardCurrencyId") && data.get("rewardCurrencyAmount"))
      rewards.push({ currencyId: data.get("rewardCurrencyId"), currencyAmount: data.get("rewardCurrencyAmount") })
    if (data.get("rewardAbilityId"))
      rewards.push({
        abilityDefinitionId: data.get("rewardAbilityId"),
        abilityCount: Number(data.get("rewardAbilityCount") || 1)
      })
    if (data.get("rewardAchievementId")) rewards.push({ achievementDefinitionId: data.get("rewardAchievementId") })
    setLoading(true)
    try {
      await api.post(`/families/${familyId}/tasks`, {
        title: data.get("title"),
        description: data.get("description"),
        assignmentStrategy: effectiveStrategy,
        assigneeAudience: assignees,
        availableFrom: localDateTimeToIso(data.get("availableFrom")),
        dueAt: localDateTimeToIso(data.get("dueAt")),
        recurrence:
          recurrence === "NONE"
            ? undefined
            : { frequency: recurrence, interval: Number(data.get("recurrenceInterval") || 1) },
        completionMode: approvalRequired ? "REQUIRE_APPROVAL" : "TRUST",
        approverAudience: approvalRequired ? approvers : undefined,
        approverMode: data.get("approverMode") || "ANY_ONE",
        checklist: String(data.get("checklist") || "")
          .split("\n")
          .map((v) => v.trim())
          .filter(Boolean),
        reminders: [60],
        overduePolicy: data.get("overduePolicy") || "KEEP_OVERDUE",
        rewards,
        penalty:
          data.get("penaltyCurrencyId") && data.get("penaltyAmount")
            ? { currencyId: data.get("penaltyCurrencyId"), amount: data.get("penaltyAmount") }
            : undefined
      })
      showToast("Задача создана")
      navigate("/tasks")
    } catch (e) {
      showToast(toApiMessage(e), "danger")
    } finally {
      setLoading(false)
    }
  }
  return (
    <div className="page-stack page-stack--form task-create-page">
      <PageHeader
        eyebrow="Задачи"
        title="Новая задача"
        description="Сначала главное. Остальные настройки можно добавить при необходимости."
      />
      <form className="form-stack" onSubmit={submit}>
        <FormSection title="Что сделать">
          <Field label="Что сделать">
            <input name="title" required maxLength={120} autoFocus placeholder="Например, убрать кухню" />
          </Field>
          <Field label="Описание">
            <textarea name="description" rows={2} placeholder="Необязательно" />
          </Field>
        </FormSection>
        <FormSection title="Кому">
          <AudienceSelector label="Кому" value={assignees} onChange={setAssignees} />
          <div>
            {assigneeCount > 1 ? (
              <Field label="Как распределить">
                <select
                  value={effectiveStrategy}
                  onChange={(event) => setStrategy(event.target.value as typeof strategy)}
                >
                  {canClaimOne ? <option value="ANY">Кто-нибудь один возьмёт задачу</option> : null}
                  <option value="ALL">Каждый участник</option>
                  <option value="ROTATION">По очереди</option>
                </select>
              </Field>
            ) : null}
          </div>
        </FormSection>
        <FormSection title="Когда" optional>
          <div className="form-grid">
            <Field label="Доступна с">
              <input name="availableFrom" type="datetime-local" />
            </Field>
            <Field label="Сделать до">
              <input name="dueAt" type="datetime-local" />
            </Field>
          </div>
        </FormSection>
        <button type="button" className="advanced-toggle" onClick={() => setAdvanced((v) => !v)}>
          {advanced ? "Скрыть дополнительные настройки" : "Дополнительные настройки"}
        </button>
        {advanced ? (
          <div className="advanced-box">
            <div className="form-grid">
              <Field label="Повтор">
                <select value={recurrence} onChange={(event) => setRecurrence(event.target.value)}>
                  <option value="NONE">Не повторять</option>
                  <option value="DAILY">Каждый день</option>
                  <option value="WEEKLY">Каждую неделю</option>
                  <option value="MONTHLY">Каждый месяц</option>
                </select>
              </Field>
              {recurrence !== "NONE" ? (
                <Field label="Интервал" hint="Например, раз в две недели">
                  <input name="recurrenceInterval" type="number" min="1" max="365" defaultValue="1" />
                </Field>
              ) : null}
            </div>
            <Field label="Чек-лист" hint="Один пункт на строку">
              <textarea name="checklist" rows={3} />
            </Field>
            <label className="check-row">
              <input
                name="approval"
                type="checkbox"
                checked={approvalRequired}
                onChange={(event) => setApprovalRequired(event.target.checked)}
              />
              <span>Нужно подтверждение выполнения</span>
            </label>
            {approvalRequired ? (
              <AudienceSelector label="Кто подтверждает" value={approvers} onChange={setApprovers} />
            ) : null}
            <div className="form-grid">
              <Field label="Награда валютой">
                <select
                  name="rewardCurrencyId"
                  value={rewardCurrencyId}
                  onChange={(event) => setRewardCurrencyId(event.target.value)}
                >
                  <option value="">Без валюты</option>
                  {collection(currencies.data).map((item) => (
                    <option key={item.id} value={item.id}>
                      {item.icon} {item.name}
                    </option>
                  ))}
                </select>
              </Field>
              {rewardCurrencyId ? (
                <Field label="Количество валюты">
                  <input name="rewardCurrencyAmount" inputMode="numeric" required />
                </Field>
              ) : null}
            </div>
            <div className="form-grid">
              <Field label="Награда способностью">
                <select
                  name="rewardAbilityId"
                  value={rewardAbilityId}
                  onChange={(event) => setRewardAbilityId(event.target.value)}
                >
                  <option value="">Без способности</option>
                  {collection(abilities.data).map((item) => (
                    <option key={item.id} value={item.id}>
                      {item.icon} {item.name}
                    </option>
                  ))}
                </select>
              </Field>
              {rewardAbilityId ? (
                <Field label="Количество способностей">
                  <input name="rewardAbilityCount" type="number" min="1" max="100" defaultValue="1" required />
                </Field>
              ) : null}
            </div>
            <Field label="Награда достижением">
              <select name="rewardAchievementId">
                <option value="">Без достижения</option>
                {collection(achievements.data).map((item) => (
                  <option key={item.id} value={item.id}>
                    {item.icon} {item.name}
                  </option>
                ))}
              </select>
            </Field>
            <div className="form-grid">
              <Field label="Штраф за пропуск">
                <select
                  name="penaltyCurrencyId"
                  value={penaltyCurrencyId}
                  onChange={(event) => setPenaltyCurrencyId(event.target.value)}
                >
                  <option value="">Без штрафа</option>
                  {collection(currencies.data).map((item) => (
                    <option key={item.id} value={item.id}>
                      {item.icon} {item.name}
                    </option>
                  ))}
                </select>
              </Field>
              {penaltyCurrencyId ? (
                <Field label="Размер штрафа">
                  <input name="penaltyAmount" inputMode="numeric" required />
                </Field>
              ) : null}
            </div>
            <Field label="Если срок прошёл">
              <select name="overduePolicy">
                <option value="KEEP_OVERDUE">Оставить просроченной</option>
                <option value="MARK_MISSED_AND_CONTINUE">Отметить пропущенной и продолжить</option>
                <option value="BLOCK_NEXT_UNTIL_CLOSED">Не создавать следующую</option>
              </select>
            </Field>
          </div>
        ) : null}
        <StickyActionBar>
          <Button type="button" variant="ghost" onClick={() => navigate("/tasks")}>
            Отмена
          </Button>
          <Button type="submit" loading={loading}>
            Создать задачу
          </Button>
        </StickyActionBar>
      </form>
    </div>
  )
}

function CompletionDialog({
  assignment,
  onClose,
  onDone
}: {
  assignment: { assignment: Assignment; item: Occurrence } | null
  onClose(): void
  onDone(): void
}) {
  const { familyId } = useFamilyId()
  const [loading, setLoading] = useState(false)
  const mediaInput = useRef<HTMLInputElement>(null)
  async function submit(e: FormEvent<HTMLFormElement>) {
    e.preventDefault()
    if (!assignment) return
    const data = new FormData(e.currentTarget)
    setLoading(true)
    try {
      const input = e.currentTarget.elements.namedItem("media") as HTMLInputElement
      const existing = assignment.assignment.completion
      const media = (data.get("keepExistingMedia") === "on" ? (existing?.media ?? []) : []) as Array<{
        url: string
        type: "IMAGE" | "VIDEO"
      }>
      for (const file of Array.from(input.files ?? [])) {
        const upload = new FormData()
        upload.append("file", await optimizeTaskPhoto(file))
        media.push(await api.upload<{ url: string; type: "IMAGE" | "VIDEO" }>(`/families/${familyId}/media`, upload))
      }
      await api.post(`/families/${familyId}/tasks/assignments/${assignment.assignment.id}/complete`, {
        comment: data.get("comment"),
        checklistState: assignment.item.definition.checklist.map((entry) => ({
          id: entry.id,
          checked: data.get(`checklist:${entry.id}`) === "on"
        })),
        media
      })
      showToast(existing ? "Результат обновлён и снова отправлен на проверку" : "Отчёт отправлен")
      onDone()
    } catch (error) {
      showToast(toApiMessage(error), "danger")
    } finally {
      setLoading(false)
    }
  }
  return (
    <Dialog
      open={Boolean(assignment)}
      onClose={onClose}
      title={assignment?.assignment.completion ? "Редактировать результат" : "Завершить задачу"}
    >
      <form className="form-stack" onSubmit={submit} key={assignment?.assignment.id ?? "closed"}>
        {assignment?.item.definition.checklist.length ? (
          <Field label="Что выполнено">
            <div className="form-stack form-stack--compact">
              {assignment.item.definition.checklist.map((entry) => (
                <label className="check-row" key={entry.id}>
                  <input
                    name={`checklist:${entry.id}`}
                    type="checkbox"
                    defaultChecked={assignment.assignment.completion?.checklistState?.some(
                      (state) => state.id === entry.id && state.checked
                    )}
                  />
                  <span>{entry.title}</span>
                </label>
              ))}
            </div>
          </Field>
        ) : null}
        <Field label="Комментарий">
          <textarea
            name="comment"
            rows={4}
            placeholder="Что получилось?"
            defaultValue={assignment?.assignment.completion?.comment ?? ""}
          />
        </Field>
        {assignment?.assignment.completion?.media.length ? (
          <Field label="Уже приложено">
            <div className="form-stack form-stack--compact">
              <TaskMediaGallery media={assignment.assignment.completion.media} />
              <label className="check-row">
                <input name="keepExistingMedia" type="checkbox" defaultChecked />
                <span>Сохранить эти вложения</span>
              </label>
            </div>
          </Field>
        ) : null}
        <Field label="Фото и видео" hint="Можно выбрать несколько файлов, до 50 МБ каждый. Большие фото оптимизируются перед отправкой.">
          <div className="file-input-row">
            <input ref={mediaInput} name="media" type="file" accept="image/*,video/*" multiple />
            <Button
              type="button"
              size="sm"
              variant="ghost"
              onClick={() => {
                if (mediaInput.current) mediaInput.current.value = ""
              }}
            >
              Очистить
            </Button>
          </div>
        </Field>
        <div className="dialog__actions">
          <Button type="button" variant="ghost" onClick={onClose}>
            Отмена
          </Button>
          <Button type="submit" loading={loading}>
            {assignment?.assignment.completion ? "Сохранить и отправить" : "Отправить"}
          </Button>
        </div>
      </form>
    </Dialog>
  )
}

export function MeV1Page() {
  const { familyId, memberId, role } = useFamilyId()
  const groups = useGroups()
  const members = useMembers()
  const [tab, setTab] = useState<"overview" | "currencies" | "shop" | "abilities" | "achievements">("overview")
  const [abilityTab, setAbilityTab] = useState<"mine" | "available">("mine")
  const [achievementTab, setAchievementTab] = useState<"mine" | "available">("mine")
  const [action, setAction] = useState<CurrencyAction | null>(null)
  const [abilityAction, setAbilityAction] = useState<{ ability: Ability; instanceId: string } | null>(null)
  const [abilityGrant, setAbilityGrant] = useState<Ability | null>(null)
  const [achievementAction, setAchievementAction] = useState<Achievement | null>(null)
  const queryClient = useQueryClient()
  const currencies = useQuery({
    queryKey: ["v1", familyId, "currencies"],
    queryFn: () => api.get<{ items: Currency[] }>(`/families/${familyId}/currencies`)
  })
  const offers = useQuery({
    queryKey: ["v1", familyId, "offers"],
    queryFn: () => api.get<{ items: Offer[] }>(`/families/${familyId}/offers`)
  })
  const abilities = useQuery({
    queryKey: ["v1", familyId, "abilities"],
    queryFn: () => api.get<{ items: Ability[] }>(`/families/${familyId}/abilities`)
  })
  const achievements = useQuery({
    queryKey: ["v1", familyId, "achievements"],
    queryFn: () => api.get<{ items: Achievement[] }>(`/families/${familyId}/achievements`)
  })
  const buy = useMutation({
    mutationFn: (id: string) =>
      api.post(`/families/${familyId}/offers/${id}/purchase`, { idempotencyKey: idempotencyKey("buy") }),
    onSuccess: () => {
      showToast("Покупка готова")
      void queryClient.invalidateQueries({ queryKey: ["v1", familyId] })
    },
    onError: (e) => showToast(toApiMessage(e), "danger")
  })
  const tabs = [
    { value: "overview" as const, label: "Обзор" },
    { value: "currencies" as const, label: "Валюты" },
    { value: "shop" as const, label: "Магазин" },
    { value: "abilities" as const, label: "Способности" },
    { value: "achievements" as const, label: "Достижения" }
  ]
  const allCurrencies = collection(currencies.data)
  const myCurrencies = allCurrencies.filter((currency) => isMyCurrency(currency, memberId, groups))
  const managedCurrencies = allCurrencies.filter(
    (currency) =>
      audienceHas(currency.redeemProviderAudience, memberId, groups) &&
      members.some((member) => member.id !== memberId && audienceHas(currency.receiveAudience, member.id, groups))
  )
  return (
    <div className="page-stack">
      <PageHeader title="Моё" />
      <Segmented value={tab} onChange={(value) => setTab(value as typeof tab)} options={tabs} />
      {tab === "overview" ? (
        <div className="me-overview">
          <section className="panel">
            <div className="section-heading">
              <div>
                <h2>Ваши валюты</h2>
                <p>То, чем можно поделиться или что-то получить</p>
              </div>
              <Button size="sm" variant="ghost" onClick={() => setTab("currencies")}>
                Все валюты
              </Button>
            </div>
            <div className="balance-list">
              {myCurrencies.map((currency) => (
                <div className="balance-row" key={currency.id}>
                  <EmojiIcon className="entity-icon" value={currency.icon} />
                  <div>
                    <strong>{currency.name}</strong>
                    <small>Личный баланс</small>
                  </div>
                  <CurrencyAmount
                    amount={amount(currency.balances.find((balance) => balance.memberId === memberId)?.amount ?? "0")}
                    symbol={currency.symbol}
                  />
                </div>
              ))}
            </div>
          </section>
          <section className="panel">
            <div className="section-heading">
              <div>
                <h2>Способности</h2>
                <p>
                  Можно использовать{" "}
                  {collection(abilities.data).reduce(
                    (count, ability) =>
                      count + ability.instances.filter((instance) => instance.status === "AVAILABLE").length,
                    0
                  )}
                </p>
              </div>
              <Button size="sm" variant="ghost" onClick={() => setTab("abilities")}>
                Открыть
              </Button>
            </div>
          </section>
          <section className="panel">
            <div className="section-heading">
              <div>
                <h2>Достижения</h2>
                <p>
                  Получено{" "}
                  {collection(achievements.data).reduce(
                    (count, achievement) =>
                      count + achievement.awards.filter((award) => award.recipient.id === memberId).length,
                    0
                  )}
                </p>
              </div>
              <Button size="sm" variant="ghost" onClick={() => setTab("achievements")}>
                Посмотреть
              </Button>
            </div>
          </section>
        </div>
      ) : null}
      {tab === "currencies" ? (
        <div className="page-stack page-stack--compact">
          <section className="page-stack page-stack--compact">
            <div className="section-heading">
              <div>
                <h2>Мои валюты</h2>
                <p>То, что можно получить или потратить</p>
              </div>
            </div>
            {myCurrencies.length ? (
              <div className="catalog-grid">
                {myCurrencies.map((currency) => (
                  <FamilyCurrencyCard
                    key={currency.id}
                    name={currency.name}
                    symbol={currency.symbol}
                    icon={currency.icon}
                    color={currency.color}
                    restricted={currency.balanceVisibility === "RESTRICTED"}
                    amount={amount(currency.balances.find((b) => b.memberId === memberId)?.amount ?? "0")}
                    actions={
                      <div className="button-row">
                        {audienceHas(currency.issueAudience, memberId, groups) ? (
                          <Button size="sm" variant="soft" onClick={() => setAction({ kind: "issue", currency })}>
                            Начислить
                          </Button>
                        ) : null}
                        {currency.transferEnabled &&
                        BigInt(currency.balances.find((b) => b.memberId === memberId)?.amount ?? 0) > 0n ? (
                          <Button size="sm" variant="soft" onClick={() => setAction({ kind: "transfer", currency })}>
                            Передать
                          </Button>
                        ) : null}
                        {BigInt(currency.balances.find((b) => b.memberId === memberId)?.amount ?? 0) > 0n &&
                        membersInAudience(currency.redeemProviderAudience, groups).some((id) => id !== memberId) ? (
                          <Button size="sm" onClick={() => setAction({ kind: "redeem", currency })}>
                            Потратить
                          </Button>
                        ) : null}
                      </div>
                    }
                  />
                ))}
              </div>
            ) : (
              <EmptyState icon="🪙" title="Пока нет валют" text="Доступные вам валюты появятся здесь" />
            )}
          </section>
          {managedCurrencies.length ? (
            <section className="page-stack page-stack--compact">
              <div className="section-heading">
                <div>
                  <h2>У меня тратят</h2>
                  <p>Балансы людей, для которых вы принимаете оплату</p>
                </div>
              </div>
              <div className="managed-currency-list">
                {managedCurrencies.map((currency) => {
                  const canIssue = audienceHas(currency.issueAudience, memberId, groups)
                  const canDeduct = audienceHas(currency.deductAudience, memberId, groups)
                  const holders = members.filter(
                    (member) => member.id !== memberId && audienceHas(currency.receiveAudience, member.id, groups)
                  )
                  return (
                    <article className="panel managed-currency-card" key={`managed:${currency.id}`}>
                      <div className="entity-identity">
                        <EmojiIcon className="entity-icon" value={currency.icon} />
                        <div>
                          <h3>{currency.name}</h3>
                          <small>Можно принять оплату у {holders.length} чел.</small>
                        </div>
                      </div>
                      <div className="managed-currency-card__people">
                        {holders.map((holder) => (
                          <div className="managed-balance-row" key={holder.id}>
                            <div className="managed-balance-row__person">
                              <strong>{holder.displayName}</strong>
                              <small>Доступно для траты</small>
                            </div>
                            <CurrencyAmount
                              amount={amount(
                                currency.balances.find((balance) => balance.memberId === holder.id)?.amount ?? "0"
                              )}
                              symbol={currency.symbol}
                            />
                            {canIssue || canDeduct ? (
                              <div className="managed-balance-row__actions">
                                {canIssue ? (
                                  <Button
                                    size="sm"
                                    variant="soft"
                                    onClick={() => setAction({ kind: "issue", currency, targetMemberId: holder.id })}
                                  >
                                    Начислить
                                  </Button>
                                ) : null}
                                {canDeduct ? (
                                  <Button
                                    size="sm"
                                    variant="danger"
                                    onClick={() => setAction({ kind: "deduct", currency, targetMemberId: holder.id })}
                                  >
                                    Штраф
                                  </Button>
                                ) : null}
                              </div>
                            ) : null}
                          </div>
                        ))}
                      </div>
                    </article>
                  )
                })}
              </div>
            </section>
          ) : null}
        </div>
      ) : null}
      {tab === "shop" ? (
        <div className="catalog-grid">
          {collection(offers.data).map((offer) => (
            <FamilyOfferCard
              key={offer.id}
              icon={offer.icon}
              title={offer.title}
              description={offer.description}
              price={<CurrencyAmount amount={amount(offer.priceAmount)} symbol={offer.priceCurrency.symbol} />}
              action={
                <Button size="sm" loading={buy.isPending} onClick={() => buy.mutate(offer.id)}>
                  Купить за {amount(offer.priceAmount)} {offer.priceCurrency.symbol}
                </Button>
              }
            />
          ))}
        </div>
      ) : null}
      {tab === "abilities" ? (
        <div className="page-stack page-stack--compact">
          <Segmented
            value={abilityTab}
            onChange={(value) => setAbilityTab(value as typeof abilityTab)}
            options={[
              { value: "mine", label: "Мои" },
              { value: "available", label: "Доступные для выдачи" }
            ]}
          />
          {abilities.isLoading ? (
            <LoadingState />
          ) : abilityTab === "mine" ? (
            <AbilityCatalog
              items={collection(abilities.data).filter((ability) => ability.instances.length > 0)}
              onUse={(ability, instanceId) => setAbilityAction({ ability, instanceId })}
              emptyTitle="Пока нет способностей"
            />
          ) : canReward(role) ? (
            <AbilityCatalog
              items={collection(abilities.data)}
              onGrant={setAbilityGrant}
              emptyTitle="Нет способностей для выдачи"
            />
          ) : (
            <EmptyState
              icon="✨"
              title="Нет способностей для выдачи"
              text="Выдавать способности могут владельцы и администраторы"
            />
          )}
        </div>
      ) : null}
      {tab === "achievements" ? (
        <div className="page-stack page-stack--compact">
          <Segmented
            value={achievementTab}
            onChange={(value) => setAchievementTab(value as typeof achievementTab)}
            options={[
              { value: "mine", label: "Мои" },
              { value: "available", label: "Доступные для выдачи" }
            ]}
          />
          {achievements.isLoading ? (
            <LoadingState />
          ) : achievements.isError ? (
            <ErrorState message={toApiMessage(achievements.error)} retry={() => void achievements.refetch()} />
          ) : achievementTab === "mine" ? (
            <AchievementCatalog
              items={collection(achievements.data).filter((item) =>
                item.awards.some((award) => award.recipient.id === memberId)
              )}
              memberId={memberId}
              emptyTitle="Пока нет достижений"
              emptyText="Полученные награды появятся здесь"
            />
          ) : canReward(role) ? (
            <AchievementCatalog
              items={collection(achievements.data)}
              memberId={memberId}
              actionLabel="Вручить"
              onAction={setAchievementAction}
              emptyTitle="Нет доступных достижений"
              emptyText="Создать их можно в разделе семьи"
            />
          ) : (
            <EmptyState
              icon="🏅"
              title="Нет достижений для выдачи"
              text="Вручать семейные достижения могут владельцы и администраторы"
            />
          )}
        </div>
      ) : null}
      <CurrencyActionDialog
        action={action}
        onClose={() => setAction(null)}
        onDone={() => {
          setAction(null)
          void queryClient.invalidateQueries({ queryKey: ["v1", familyId] })
        }}
      />
      <AbilityUseDialog
        action={abilityAction}
        onClose={() => setAbilityAction(null)}
        onDone={() => {
          setAbilityAction(null)
          void queryClient.invalidateQueries({ queryKey: ["v1", familyId] })
        }}
      />
      <AbilityGrantDialog
        ability={abilityGrant}
        members={members}
        groups={groups}
        onClose={() => setAbilityGrant(null)}
        onDone={() => {
          setAbilityGrant(null)
          void queryClient.invalidateQueries({ queryKey: ["v1", familyId, "abilities"] })
        }}
      />
      <AchievementAwardDialog
        achievement={achievementAction}
        members={members}
        onClose={() => setAchievementAction(null)}
        onDone={() => {
          setAchievementAction(null)
          void queryClient.invalidateQueries({ queryKey: ["v1", familyId, "achievements"] })
        }}
      />
    </div>
  )
}

function AbilityCatalog({
  items,
  onUse,
  onGrant,
  emptyTitle
}: {
  items: Ability[]
  onUse?(ability: Ability, instanceId: string): void
  onGrant?(ability: Ability): void
  emptyTitle: string
}) {
  if (!items.length) return <EmptyState icon="✨" title={emptyTitle} text="Они появятся здесь после выдачи" />
  return (
    <div className="catalog-grid">
      {items.map((ability) => {
        const available = ability.instances.filter((instance) => instance.status === "AVAILABLE")
        return (
          <FamilyAbilityCard
            key={ability.id}
            icon={ability.icon}
            title={ability.name}
            description={ability.description}
            rarity={ability.rarity}
            count={available.length}
            color={ability.color}
            status={
              ability.instances.some((instance) => instance.status === "PENDING")
                ? "Ждёт подтверждения"
                : available.length
                  ? "Доступна"
                  : "Использована"
            }
            action={
              <>
                {onUse && available[0] ? (
                  <Button size="sm" onClick={() => onUse(ability, available[0]!.id)}>
                    Использовать
                  </Button>
                ) : null}
                {onGrant ? (
                  <Button size="sm" onClick={() => onGrant(ability)}>
                    Выдать
                  </Button>
                ) : null}
              </>
            }
          />
        )
      })}
    </div>
  )
}

function AbilityGrantDialog({
  ability,
  members,
  groups,
  onClose,
  onDone
}: {
  ability: Ability | null
  members: FamilyMember[]
  groups: Group[]
  onClose(): void
  onDone(): void
}) {
  const { familyId } = useFamilyId()
  const [loading, setLoading] = useState(false)
  const eligible = ability ? members.filter((member) => audienceHas(ability.holderAudience, member.id, groups)) : []
  async function submit(event: FormEvent<HTMLFormElement>) {
    event.preventDefault()
    if (!ability) return
    const data = new FormData(event.currentTarget)
    setLoading(true)
    try {
      await api.post(`/families/${familyId}/abilities/${ability.id}/grant`, {
        recipientMemberId: data.get("memberId"),
        count: Number(data.get("count") || 1),
        reason: data.get("reason"),
        idempotencyKey: idempotencyKey("ability")
      })
      showToast("Способность выдана")
      onDone()
    } catch (error) {
      showToast(toApiMessage(error), "danger")
    } finally {
      setLoading(false)
    }
  }
  return (
    <Dialog
      open={Boolean(ability)}
      onClose={onClose}
      title={ability ? `Выдать ${ability.icon} ${ability.name}` : "Выдать способность"}
    >
      <form className="form-stack" onSubmit={submit}>
        <Field label="Кому">
          <select name="memberId" required autoFocus>
            <option value="">Выберите человека</option>
            {eligible.map((member) => (
              <option key={member.id} value={member.id}>
                {member.displayName}
              </option>
            ))}
          </select>
        </Field>
        <Field label="Количество">
          <input name="count" type="number" min="1" max="100" defaultValue="1" required />
        </Field>
        <Field label="За что">
          <textarea name="reason" maxLength={500} rows={3} />
        </Field>
        <div className="dialog__actions">
          <Button type="button" variant="ghost" onClick={onClose}>
            Отмена
          </Button>
          <Button type="submit" loading={loading}>
            Выдать
          </Button>
        </div>
      </form>
    </Dialog>
  )
}

function AchievementCatalog({
  items,
  memberId,
  actionLabel,
  onAction,
  emptyTitle,
  emptyText
}: {
  items: Achievement[]
  memberId: string
  actionLabel?: string
  onAction?(achievement: Achievement): void
  emptyTitle: string
  emptyText: string
}) {
  if (!items.length) return <EmptyState icon="🏅" title={emptyTitle} text={emptyText} />
  return (
    <div className="catalog-grid">
      {items.map((item) => {
        const received = item.awards.filter((award) => award.recipient.id === memberId).length
        return (
          <FamilyAchievementCard
            key={item.id}
            icon={item.icon}
            title={item.name}
            description={item.description}
            rarity={item.rarity}
            awarded={received > 0}
            action={
              <>
                {received ? <small>Получено: {received}</small> : null}
                {actionLabel && onAction ? (
                  <Button size="sm" onClick={() => onAction(item)}>
                    {actionLabel}
                  </Button>
                ) : null}
              </>
            }
          />
        )
      })}
    </div>
  )
}

function AchievementAwardDialog({
  achievement,
  members,
  onClose,
  onDone
}: {
  achievement: Achievement | null
  members: FamilyMember[]
  onClose(): void
  onDone(): void
}) {
  const { familyId } = useFamilyId()
  const [loading, setLoading] = useState(false)
  async function submit(event: FormEvent<HTMLFormElement>) {
    event.preventDefault()
    if (!achievement) return
    const data = new FormData(event.currentTarget)
    setLoading(true)
    try {
      await api.post(`/families/${familyId}/achievements/${achievement.id}/award`, {
        recipientMemberId: data.get("memberId"),
        count: Number(data.get("count") || 1),
        reason: data.get("reason"),
        idempotencyKey: idempotencyKey("achievement")
      })
      const count = Number(data.get("count") || 1)
      showToast(count > 1 ? `Достижения вручены ×${count}` : "Достижение вручено")
      onDone()
    } catch (error) {
      showToast(toApiMessage(error), "danger")
    } finally {
      setLoading(false)
    }
  }
  return (
    <Dialog
      open={Boolean(achievement)}
      onClose={onClose}
      title={achievement ? `Вручить ${achievement.icon} ${achievement.name}` : "Вручить достижение"}
    >
      <form className="form-stack" onSubmit={submit}>
        <Field label="Кому">
          <select name="memberId" required autoFocus>
            <option value="">Выберите человека</option>
            {members.map((member) => (
              <option key={member.id} value={member.id}>
                {member.displayName}
              </option>
            ))}
          </select>
        </Field>
        <Field label="Количество">
          <input name="count" type="number" inputMode="numeric" min="1" max="100" defaultValue="1" required />
        </Field>
        <Field label="За что">
          <textarea name="reason" required maxLength={500} rows={3} placeholder="Коротко опишите повод" />
        </Field>
        <div className="dialog__actions">
          <Button type="button" variant="ghost" onClick={onClose}>
            Отмена
          </Button>
          <Button type="submit" loading={loading}>
            Вручить
          </Button>
        </div>
      </form>
    </Dialog>
  )
}

function CurrencyActionDialog({
  action,
  onClose,
  onDone
}: {
  action: CurrencyAction | null
  onClose(): void
  onDone(): void
}) {
  const { familyId, memberId } = useFamilyId()
  const members = useMembers()
  const groups = useGroups()
  const [loading, setLoading] = useState(false)
  const labels = { issue: "Начислить", transfer: "Передать", redeem: "Потратить", deduct: "Выписать штраф" }
  const allowedMembers = action
    ? members.filter((member) => {
        if (member.id === memberId) return false
        if (action.kind === "issue") return audienceHas(action.currency.receiveAudience, member.id, groups)
        if (action.kind === "redeem") return audienceHas(action.currency.redeemProviderAudience, member.id, groups)
        return true
      })
    : members
  async function submit(e: FormEvent<HTMLFormElement>) {
    e.preventDefault()
    if (!action) return
    const data = new FormData(e.currentTarget)
    setLoading(true)
    try {
      const target = String(data.get("memberId"))
      const base = {
        amount: data.get("amount"),
        reason: data.get("reason"),
        comment: data.get("reason"),
        idempotencyKey: idempotencyKey(action.kind)
      }
      const body =
        action.kind === "issue"
          ? { ...base, recipientMemberId: target }
          : action.kind === "transfer"
            ? { ...base, recipientMemberId: target }
            : action.kind === "deduct"
              ? { ...base, targetMemberId: target }
              : { ...base, providerMemberId: target }
      await api.post(`/families/${familyId}/currencies/${action.currency.id}/${action.kind}`, body)
      showToast(
        action.kind === "redeem" && action.currency.redeemProviderApprovalRequired
          ? "Запрос отправлен на подтверждение"
          : "Готово"
      )
      onDone()
    } catch (error) {
      showToast(toApiMessage(error), "danger")
    } finally {
      setLoading(false)
    }
  }
  return (
    <Dialog
      open={Boolean(action)}
      onClose={onClose}
      title={action ? `${labels[action.kind]} ${action.currency.icon} ${action.currency.name}` : "Действие с валютой"}
    >
      <form className="form-stack" onSubmit={submit}>
        <Field
          label={
            action?.kind === "redeem" ? "У кого потратить" : action?.kind === "deduct" ? "Кому выписать штраф" : "Кому"
          }
        >
          <select name="memberId" required defaultValue={action?.targetMemberId ?? ""}>
            <option value="">Выберите человека</option>
            {allowedMembers.map((m) => (
              <option key={m.id} value={m.id}>
                {m.displayName}
              </option>
            ))}
          </select>
        </Field>
        <Field label="Сколько">
          <input name="amount" required inputMode="numeric" pattern="[0-9]+" />
        </Field>
        <Field label="Комментарий">
          <input name="reason" maxLength={500} />
        </Field>
        <div className="dialog__actions">
          <Button type="button" variant="ghost" onClick={onClose}>
            Отмена
          </Button>
          <Button type="submit" loading={loading}>
            {action ? labels[action.kind] : "Готово"}
          </Button>
        </div>
      </form>
    </Dialog>
  )
}

function AbilityUseDialog({
  action,
  onClose,
  onDone
}: {
  action: { ability: Ability; instanceId: string } | null
  onClose(): void
  onDone(): void
}) {
  const { familyId, memberId } = useFamilyId()
  const members = useMembers()
  const tasks = useQuery({
    queryKey: ["v1", familyId, "tasks", "ability"],
    queryFn: () => api.get<{ items: Occurrence[] }>(`/families/${familyId}/tasks?view=plan`),
    enabled: Boolean(action?.ability.effect === "SKIP_TASK")
  })
  const [loading, setLoading] = useState(false)
  const assignments = sortByClosestDate(collection(tasks.data), (occurrence) => occurrence.dueAt).flatMap((occurrence) =>
    occurrence.assignments
      .filter((assignment) => assignment.memberId === memberId && ["OPEN", "CLAIMED"].includes(assignment.status))
      .map((assignment) => ({ id: assignment.id, title: occurrence.definition.title }))
  )
  async function submit(event: FormEvent<HTMLFormElement>) {
    event.preventDefault()
    if (!action) return
    const data = new FormData(event.currentTarget)
    setLoading(true)
    try {
      await api.post(`/families/${familyId}/ability-instances/${action.instanceId}/use`, {
        targetMemberId: data.get("targetMemberId") || undefined,
        taskAssignmentId: data.get("taskAssignmentId") || undefined,
        comment: data.get("comment") || ""
      })
      showToast("Способность активирована")
      onDone()
    } catch (error) {
      showToast(toApiMessage(error), "danger")
    } finally {
      setLoading(false)
    }
  }
  return (
    <Dialog
      open={Boolean(action)}
      onClose={onClose}
      title={action ? `${action.ability.icon} ${action.ability.name}` : "Использовать способность"}
    >
      <form className="form-stack" onSubmit={submit}>
        {action?.ability.targetRequired ? (
          <Field label="На кого использовать">
            <select name="targetMemberId" required>
              <option value="">Выберите человека</option>
              {members.map((member) => (
                <option key={member.id} value={member.id}>
                  {member.displayName}
                </option>
              ))}
            </select>
          </Field>
        ) : null}
        {action?.ability.effect === "SKIP_TASK" ? (
          <Field label="Какую задачу пропустить">
            <select name="taskAssignmentId" required>
              <option value="">Выберите задачу</option>
              {assignments.map((assignment) => (
                <option key={assignment.id} value={assignment.id}>
                  {assignment.title}
                </option>
              ))}
            </select>
          </Field>
        ) : null}
        <Field label="Комментарий">
          <textarea name="comment" rows={3} />
        </Field>
        <div className="dialog__actions">
          <Button type="button" variant="ghost" onClick={onClose}>
            Отмена
          </Button>
          <Button type="submit" loading={loading}>
            Использовать
          </Button>
        </div>
      </form>
    </Dialog>
  )
}

export function FamilyV1Page() {
  const { familyId, memberId, role } = useFamilyId()
  const navigate = useNavigate()
  const [tab, setTab] = useState<"people" | "groups" | "settings">("people")
  const [inviteOpen, setInviteOpen] = useState(false)
  const queryClient = useQueryClient()
  const membersQuery = useQuery({
    queryKey: ["members", familyId],
    queryFn: () => api.get<{ items: FamilyMember[] }>(`/families/${familyId}/members`)
  })
  const groupsQuery = useQuery({
    queryKey: ["v1", familyId, "groups"],
    queryFn: () => api.get<{ items: Group[] }>(`/families/${familyId}/groups`)
  })
  const members = collection(membersQuery.data),
    groups = collection(groupsQuery.data)
  const manage = role === "OWNER" || role === "ADMIN"
  async function updateMember(targetId: string, action: "role" | "status", value: string) {
    try {
      await api.patch(`/families/${familyId}/members/${targetId}/${action}`, {
        ...(action === "role" ? { role: value } : { status: value }),
        reason: action === "role" ? "Изменение роли в настройках семьи" : "Изменение доступа в настройках семьи"
      })
      showToast("Участник обновлён")
      await queryClient.invalidateQueries({ queryKey: ["members", familyId] })
    } catch (error) {
      showToast(toApiMessage(error), "danger")
    }
  }
  async function deleteGroup(group: Group) {
    if (!window.confirm(`Удалить группу «${group.name}»?`)) return
    try {
      await api.delete(`/families/${familyId}/groups/${group.id}`)
      showToast("Группа удалена")
      await queryClient.invalidateQueries({ queryKey: ["v1", familyId, "groups"] })
    } catch (error) {
      showToast(toApiMessage(error), "danger")
    }
  }
  return (
    <div className="page-stack">
      <PageHeader
        title="Семья"
        actions={
          manage ? (
            <Button icon="plus" onClick={() => setInviteOpen(true)}>
              Пригласить
            </Button>
          ) : null
        }
      />
      <Segmented
        value={tab}
        onChange={(value) => setTab(value as typeof tab)}
        options={[
          { value: "people", label: "Люди" },
          { value: "groups", label: "Группы" },
          { value: "settings", label: "Настройки" }
        ]}
      />
      {tab === "people" ? (
        <div className="people-list">
          {members.map((member) => (
            <article className="person-row" key={member.id}>
              <PersonAvatar person={{ id: member.id, displayName: member.displayName }} />
              <div>
                <strong>{member.displayName}</strong>
                <small>
                  {member.role === "OWNER" ? "Владелец" : member.role === "ADMIN" ? "Администратор" : "Участник"}
                </small>
              </div>
              <Badge tone={member.status === "ACTIVE" ? "success" : "warning"}>
                {member.status === "ACTIVE" ? "Активен" : "Неактивен"}
              </Badge>
              {manage && member.id !== memberId && !(role === "ADMIN" && member.role === "OWNER") ? (
                <div className="button-row">
                  <select
                    aria-label={`Роль ${member.displayName}`}
                    value={member.role}
                    onChange={(event) => void updateMember(member.id, "role", event.target.value)}
                  >
                    {role === "OWNER" ? <option value="OWNER">Владелец</option> : null}
                    <option value="ADMIN">Администратор</option>
                    <option value="MEMBER">Участник</option>
                  </select>
                  <Button
                    size="sm"
                    variant={member.status === "ACTIVE" ? "danger" : "secondary"}
                    onClick={() =>
                      void updateMember(member.id, "status", member.status === "ACTIVE" ? "SUSPENDED" : "ACTIVE")
                    }
                  >
                    {member.status === "ACTIVE" ? "Отключить" : "Вернуть"}
                  </Button>
                </div>
              ) : null}
            </article>
          ))}
        </div>
      ) : null}
      {tab === "groups" ? (
        <>
          <div className="section-heading">
            <div>
              <h2>Группы</h2>
              <p>Используйте их в задачах, валютах и подтверждениях</p>
            </div>
            {manage ? (
              <Button icon="plus" onClick={() => navigate("/family/config/group/new")}>
                Новая группа
              </Button>
            ) : null}
          </div>
          <div className="catalog-grid group-grid">
            {groups.map((group) => (
              <article className="catalog-card" key={group.id}>
                <span className="catalog-emoji">{group.isSystem ? "🏡" : "👥"}</span>
                <h3>{group.name}</h3>
                <p>{group.memberships.length} участников</p>
                <small className="group-members-preview">
                  {group.memberships
                    .map((membership) => members.find((member) => member.id === membership.memberId)?.displayName)
                    .filter(Boolean)
                    .join(", ") || "Нет участников"}
                </small>
                {group.isSystem ? <Badge>Системная</Badge> : null}
                {manage && !group.isSystem ? (
                  <div className="button-row">
                    <Button size="sm" variant="ghost" onClick={() => navigate(`/family/config/group/${group.id}/edit`)}>
                      Редактировать
                    </Button>
                    <Button size="sm" variant="danger" onClick={() => void deleteGroup(group)}>
                      Удалить
                    </Button>
                  </div>
                ) : null}
              </article>
            ))}
          </div>
        </>
      ) : null}
      {tab === "settings" ? <FamilySettings manage={manage} /> : null}
      <InviteMemberSheet open={inviteOpen} onClose={() => setInviteOpen(false)} />
    </div>
  )
}

// eslint-disable-next-line @typescript-eslint/no-unused-vars
function LegacyGroupDialog({
  value: group,
  onClose,
  onDone
}: {
  value: Group | "new" | null
  onClose(): void
  onDone(): void
}) {
  const { familyId } = useFamilyId()
  const [value, setValue] = useState<Audience>(() => ({
    memberIds: group && group !== "new" ? group.memberships.map((membership) => membership.memberId) : [],
    groupIds: []
  }))
  async function submit(e: FormEvent<HTMLFormElement>) {
    e.preventDefault()
    const data = new FormData(e.currentTarget)
    try {
      const body = { name: data.get("name"), memberIds: value.memberIds }
      if (group && group !== "new") await api.patch(`/families/${familyId}/groups/${group.id}`, body)
      else await api.post(`/families/${familyId}/groups`, body)
      showToast(group && group !== "new" ? "Группа обновлена" : "Группа создана")
      onDone()
    } catch (error) {
      showToast(toApiMessage(error), "danger")
    }
  }
  return (
    <Dialog
      open={Boolean(group)}
      onClose={onClose}
      title={group && group !== "new" ? "Редактировать группу" : "Новая группа"}
    >
      <form className="form-stack" onSubmit={submit} key={group === "new" ? "new" : group?.id}>
        <Field label="Название">
          <input
            name="name"
            required
            placeholder="Например, Дежурные"
            defaultValue={group && group !== "new" ? group.name : ""}
          />
        </Field>
        <AudienceSelector label="Кто входит" value={value} onChange={setValue} membersOnly />
        <div className="dialog__actions">
          <Button type="button" variant="ghost" onClick={onClose}>
            Отмена
          </Button>
          <Button type="submit">{group && group !== "new" ? "Сохранить" : "Создать"}</Button>
        </div>
      </form>
    </Dialog>
  )
}

function FamilySettings({ manage }: { manage: boolean }) {
  const { familyId } = useFamilyId()
  const navigate = useNavigate()
  const client = useQueryClient()
  const currencies = useQuery({
    queryKey: ["v1", familyId, "currencies"],
    queryFn: () => api.get<{ items: Currency[] }>(`/families/${familyId}/currencies`)
  })
  const abilities = useQuery({
    queryKey: ["v1", familyId, "abilities"],
    queryFn: () => api.get<{ items: Ability[] }>(`/families/${familyId}/abilities`)
  })
  const offers = useQuery({
    queryKey: ["v1", familyId, "offers"],
    queryFn: () => api.get<{ items: Offer[] }>(`/families/${familyId}/offers`)
  })
  const achievements = useQuery({
    queryKey: ["v1", familyId, "achievements"],
    queryFn: () => api.get<{ items: Achievement[] }>(`/families/${familyId}/achievements`)
  })
  const scheduled = useQuery({
    queryKey: ["v1", familyId, "scheduled-grants"],
    queryFn: () =>
      api.get<{ items: Array<{ id: string; name: string; icon?: string }> }>(`/families/${familyId}/scheduled-grants`)
  })
  const refresh = () => client.invalidateQueries({ queryKey: ["v1", familyId] })
  async function archive(
    kind: "currencies" | "abilities" | "offers" | "achievements",
    item: { id: string; name?: string; title?: string }
  ) {
    if (!window.confirm(`Убрать «${item.name ?? item.title}» из активных? История операций сохранится.`)) return
    try {
      await api.delete(`/families/${familyId}/${kind}/${item.id}`)
      showToast("Убрано из активных")
      await refresh()
    } catch (error) {
      showToast(toApiMessage(error), "danger")
    }
  }
  if (!manage)
    return (
      <EmptyState
        icon="🔒"
        title="Настройки доступны администраторам"
        text="Вы всё равно можете пользоваться семейными правилами"
      />
    )
  return (
    <>
      <div className="settings-grid">
        <button className="setting-card" onClick={() => navigate("/family/config/currency/new")}>
          <span>🪙</span>
          <strong>Валюты</strong>
          <small>Создание и правила</small>
        </button>
        <button className="setting-card" onClick={() => navigate("/family/config/ability/new")}>
          <span>🎁</span>
          <strong>Способности</strong>
          <small>Создать новую способность</small>
        </button>
        <button className="setting-card" onClick={() => navigate("/family/config/offer/new")}>
          <span>🛍️</span>
          <strong>Магазин</strong>
          <small>Добавить предложение</small>
        </button>
        <button className="setting-card" onClick={() => navigate("/family/config/achievement/new")}>
          <span>🏆</span>
          <strong>Достижения</strong>
          <small>Семейные награды</small>
        </button>
        <button className="setting-card" onClick={() => navigate("/family/config/scheduled-grant/new")}>
          <span>🗓️</span>
          <strong>Выдачи</strong>
          <small>По расписанию</small>
        </button>
      </div>
      <section className="panel management-panel">
        <h2>Созданные правила</h2>
        {[
          { kind: "currencies" as const, title: "Валюты", items: collection(currencies.data) },
          { kind: "abilities" as const, title: "Способности", items: collection(abilities.data) },
          { kind: "offers" as const, title: "Магазин", items: collection(offers.data) },
          { kind: "achievements" as const, title: "Достижения", items: collection(achievements.data) },
          { kind: "scheduled-grants" as const, title: "Выдачи", items: collection(scheduled.data) }
        ].map((section) => (
          <div className="management-section" key={section.kind}>
            <h3>{section.title}</h3>
            {section.items.length ? (
              section.items.map((item) => (
                <div className="management-row" key={item.id}>
                  <span className="catalog-emoji">{"icon" in item && item.icon ? item.icon : "🗓️"}</span>
                  <strong>{"name" in item ? item.name : item.title}</strong>
                  <div className="button-row">
                    <Button
                      size="sm"
                      variant="ghost"
                      onClick={() =>
                        navigate(
                          `/family/config/${section.kind === "currencies" ? "currency" : section.kind === "abilities" ? "ability" : section.kind === "offers" ? "offer" : section.kind === "achievements" ? "achievement" : "scheduled-grant"}/${item.id}/edit`
                        )
                      }
                    >
                      Редактировать
                    </Button>
                    {section.kind !== "scheduled-grants" ? (
                      <Button size="sm" variant="danger" onClick={() => void archive(section.kind, item)}>
                        Удалить
                      </Button>
                    ) : null}
                  </div>
                </div>
              ))
            ) : (
              <small>Пока пусто</small>
            )}
          </div>
        ))}
      </section>
    </>
  )
}

type ManagedKind = "currencies" | "abilities" | "offers" | "achievements"
type ManagedItem = {
  id: string
  name?: string
  title?: string
  icon: string
  description: string
  symbol?: string
  transferEnabled?: boolean
  priceAmount?: string
  issueAudience?: Audience
  receiveAudience?: Audience
  deductAudience?: Audience
  redeemProviderAudience?: Audience
  redeemProviderApprovalRequired?: boolean
  balanceVisibility?: "FAMILY" | "RESTRICTED"
  holderAudience?: Audience
  targetAudience?: Audience
  approverAudience?: Audience
  targetRequired?: boolean
  requireTargetApproval?: boolean
  approverMode?: "ANY_ONE" | "ALL"
  rewardConfig?: Achievement["rewardConfig"]
}

// eslint-disable-next-line @typescript-eslint/no-unused-vars
function LegacyManagementEditDialog({
  value,
  onClose,
  onDone
}: {
  value: { kind: ManagedKind; item: ManagedItem } | null
  onClose(): void
  onDone(): void
}) {
  const { familyId } = useFamilyId()
  const [loading, setLoading] = useState(false)
  const currencies = useQuery({
    queryKey: ["v1", familyId, "currencies"],
    queryFn: () => api.get<{ items: Currency[] }>(`/families/${familyId}/currencies`)
  })
  const abilities = useQuery({
    queryKey: ["v1", familyId, "abilities"],
    queryFn: () => api.get<{ items: Ability[] }>(`/families/${familyId}/abilities`)
  })
  const [issueAudience, setIssueAudience] = useState<Audience>(
    value?.item.issueAudience ?? { memberIds: [], groupIds: [] }
  )
  const [receiveAudience, setReceiveAudience] = useState<Audience>(
    value?.item.receiveAudience ?? { memberIds: [], groupIds: [] }
  )
  const [deductAudience, setDeductAudience] = useState<Audience>(
    value?.item.deductAudience ?? { memberIds: [], groupIds: [] }
  )
  const [providerAudience, setProviderAudience] = useState<Audience>(
    value?.item.redeemProviderAudience ?? { memberIds: [], groupIds: [] }
  )
  const [holderAudience, setHolderAudience] = useState<Audience>(
    value?.item.holderAudience ?? { memberIds: [], groupIds: [] }
  )
  const [targetAudience, setTargetAudience] = useState<Audience>(
    value?.item.targetAudience ?? { memberIds: [], groupIds: [] }
  )
  const [approverAudience, setApproverAudience] = useState<Audience>(
    value?.item.approverAudience ?? { memberIds: [], groupIds: [] }
  )
  async function submit(event: FormEvent<HTMLFormElement>) {
    event.preventDefault()
    if (!value) return
    const data = new FormData(event.currentTarget)
    setLoading(true)
    try {
      await api.patch(`/families/${familyId}/${value.kind}/${value.item.id}`, {
        ...(value.kind === "offers" ? { title: data.get("name") } : { name: data.get("name") }),
        icon: data.get("icon"),
        description: data.get("description"),
        ...(value.kind === "currencies"
          ? {
              symbol: data.get("icon"),
              transferEnabled: data.get("transferEnabled") === "on",
              redeemProviderApprovalRequired: data.get("redeemProviderApprovalRequired") === "on",
              balanceVisibility: data.get("balanceVisibility"),
              issueAudience,
              receiveAudience,
              deductAudience,
              redeemProviderAudience: providerAudience
            }
          : {}),
        ...(value.kind === "abilities"
          ? {
              holderAudience,
              targetRequired: data.get("targetRequired") === "on",
              targetAudience: data.get("targetRequired") === "on" ? targetAudience : undefined,
              requireTargetApproval: data.get("requireTargetApproval") === "on",
              approverAudience,
              approverMode: data.get("approverMode")
            }
          : {}),
        ...(value.kind === "offers" ? { priceAmount: data.get("priceAmount") } : {}),
        ...(value.kind === "achievements"
          ? {
              rewardConfig:
                data.get("rewardCurrencyId") || data.get("rewardAbilityId")
                  ? {
                      ...(data.get("rewardCurrencyId") && data.get("rewardCurrencyAmount")
                        ? {
                            currency: {
                              currencyId: data.get("rewardCurrencyId"),
                              amount: data.get("rewardCurrencyAmount")
                            }
                          }
                        : {}),
                      ...(data.get("rewardAbilityId")
                        ? {
                            ability: {
                              definitionId: data.get("rewardAbilityId"),
                              count: Number(data.get("rewardAbilityCount") || 1)
                            }
                          }
                        : {})
                    }
                  : null
            }
          : {})
      })
      showToast("Изменения сохранены")
      onDone()
    } catch (error) {
      showToast(toApiMessage(error), "danger")
    } finally {
      setLoading(false)
    }
  }
  return (
    <Dialog open={Boolean(value)} onClose={onClose} title="Редактирование" size="lg">
      {value ? (
        <form className="form-stack" onSubmit={submit} key={`${value.kind}:${value.item.id}`}>
          <div className="form-grid">
            <Field label="Название">
              <input name="name" required defaultValue={value.item.name ?? value.item.title} />
            </Field>
            <Field label="Эмодзи">
              <input name="icon" required defaultValue={value.item.icon} maxLength={16} />
            </Field>
          </div>
          <Field label="Описание">
            <textarea name="description" rows={3} defaultValue={value.item.description} />
          </Field>
          {value.kind === "currencies" ? (
            <>
              <div className="advanced-box">
                <strong>Права валюты по группам и людям</strong>
                <p className="muted">
                  Можно выбрать одну или несколько групп и при необходимости добавить отдельных участников.
                </p>
              </div>
              <AudienceSelector label="Кто может начислять" value={issueAudience} onChange={setIssueAudience} />
              <AudienceSelector
                label="Кто может получать и тратить"
                value={receiveAudience}
                onChange={setReceiveAudience}
              />
              <AudienceSelector
                label="Кто может назначать штрафы"
                value={deductAudience}
                onChange={setDeductAudience}
              />
              <AudienceSelector label="Кто принимает оплату" value={providerAudience} onChange={setProviderAudience} />
              <label className="check-row">
                <input name="transferEnabled" type="checkbox" defaultChecked={value.item.transferEnabled} />
                <span>Можно передавать между участниками</span>
              </label>
              <label className="check-row">
                <input
                  name="redeemProviderApprovalRequired"
                  type="checkbox"
                  defaultChecked={value.item.redeemProviderApprovalRequired}
                />
                <span>Трата требует подтверждения выбранного человека</span>
              </label>
              <Field label="Кто видит балансы">
                <select name="balanceVisibility" defaultValue={value.item.balanceVisibility ?? "FAMILY"}>
                  <option value="FAMILY">Вся семья</option>
                  <option value="RESTRICTED">Только владелец и участники правил</option>
                </select>
              </Field>
            </>
          ) : null}
          {value.kind === "abilities" ? (
            <>
              <AudienceSelector label="Кому можно выдавать" value={holderAudience} onChange={setHolderAudience} />
              <label className="check-row">
                <input name="targetRequired" type="checkbox" defaultChecked={value.item.targetRequired} />
                <span>При использовании нужно выбрать человека</span>
              </label>
              <AudienceSelector
                label="На кого можно использовать"
                value={targetAudience}
                onChange={setTargetAudience}
              />
              <label className="check-row">
                <input name="requireTargetApproval" type="checkbox" defaultChecked={value.item.requireTargetApproval} />
                <span>Нужно подтверждение выбранного человека</span>
              </label>
              <AudienceSelector label="Кто ещё подтверждает" value={approverAudience} onChange={setApproverAudience} />
              <Field label="Сколько подтверждений нужно">
                <select name="approverMode" defaultValue={value.item.approverMode ?? "ANY_ONE"}>
                  <option value="ANY_ONE">Одного достаточно</option>
                  <option value="ALL">Нужны все</option>
                </select>
              </Field>
            </>
          ) : null}
          {value.kind === "offers" ? (
            <Field label="Цена">
              <input
                name="priceAmount"
                required
                inputMode="numeric"
                pattern="[0-9]+"
                defaultValue={value.item.priceAmount}
              />
            </Field>
          ) : null}
          {value.kind === "achievements" ? (
            <>
              <div className="form-grid">
                <Field label="Бонус валютой">
                  <select name="rewardCurrencyId" defaultValue={value.item.rewardConfig?.currency?.currencyId ?? ""}>
                    <option value="">Без валюты</option>
                    {collection(currencies.data).map((currency) => (
                      <option key={currency.id} value={currency.id}>
                        {currency.icon} {currency.name}
                      </option>
                    ))}
                  </select>
                </Field>
                <Field label="Количество">
                  <input
                    name="rewardCurrencyAmount"
                    inputMode="numeric"
                    defaultValue={value.item.rewardConfig?.currency?.amount ?? ""}
                  />
                </Field>
              </div>
              <div className="form-grid">
                <Field label="Бонус способностью">
                  <select name="rewardAbilityId" defaultValue={value.item.rewardConfig?.ability?.definitionId ?? ""}>
                    <option value="">Без способности</option>
                    {collection(abilities.data).map((ability) => (
                      <option key={ability.id} value={ability.id}>
                        {ability.icon} {ability.name}
                      </option>
                    ))}
                  </select>
                </Field>
                <Field label="Количество">
                  <input
                    name="rewardAbilityCount"
                    type="number"
                    min="1"
                    max="100"
                    defaultValue={value.item.rewardConfig?.ability?.count ?? 1}
                  />
                </Field>
              </div>
            </>
          ) : null}
          <div className="dialog__actions">
            <Button type="button" variant="ghost" onClick={onClose}>
              Отмена
            </Button>
            <Button type="submit" loading={loading}>
              Сохранить
            </Button>
          </div>
        </form>
      ) : null}
    </Dialog>
  )
}

// eslint-disable-next-line @typescript-eslint/no-unused-vars
function LegacyCatalogCreateDialog({
  kind,
  onClose,
  onDone
}: {
  kind: "ability" | "offer" | "achievement" | "scheduled" | null
  onClose(): void
  onDone(): void
}) {
  const { familyId } = useFamilyId()
  const groups = useGroups()
  const everyone = {
    memberIds: [] as string[],
    groupIds: groups.filter((group) => group.isSystem).map((group) => group.id)
  }
  const [holderAudience, setHolderAudience] = useState<Audience>(everyone)
  const [targetAudience, setTargetAudience] = useState<Audience>(everyone)
  const [approverAudience, setApproverAudience] = useState<Audience>({ memberIds: [], groupIds: [] })
  const currencies = useQuery({
    queryKey: ["v1", familyId, "currencies"],
    queryFn: () => api.get<{ items: Currency[] }>(`/families/${familyId}/currencies`)
  })
  const abilities = useQuery({
    queryKey: ["v1", familyId, "abilities"],
    queryFn: () => api.get<{ items: Ability[] }>(`/families/${familyId}/abilities`)
  })
  const titles = {
    ability: "Новая способность",
    offer: "Новое предложение",
    achievement: "Новое достижение",
    scheduled: "Выдача по расписанию"
  }
  async function submit(event: FormEvent<HTMLFormElement>) {
    event.preventDefault()
    if (!kind) return
    const data = new FormData(event.currentTarget)
    try {
      if (kind === "ability")
        await api.post(`/families/${familyId}/abilities`, {
          name: data.get("name"),
          description: data.get("description"),
          icon: data.get("icon") || "✨",
          rarity: data.get("rarity"),
          holderAudience,
          targetRequired: data.get("targetRequired") === "on",
          targetAudience: data.get("targetRequired") === "on" ? targetAudience : undefined,
          requireTargetApproval: data.get("requireTargetApproval") === "on",
          approverAudience:
            approverAudience.memberIds.length || approverAudience.groupIds.length ? approverAudience : undefined,
          approverMode: data.get("approverMode"),
          effect: data.get("effect")
        })
      if (kind === "achievement")
        await api.post(`/families/${familyId}/achievements`, {
          name: data.get("name"),
          description: data.get("description"),
          icon: data.get("icon") || "🏆",
          rarity: data.get("rarity"),
          rewardConfig:
            data.get("rewardCurrencyId") || data.get("rewardAbilityId")
              ? {
                  ...(data.get("rewardCurrencyId") && data.get("rewardCurrencyAmount")
                    ? {
                        currency: { currencyId: data.get("rewardCurrencyId"), amount: data.get("rewardCurrencyAmount") }
                      }
                    : {}),
                  ...(data.get("rewardAbilityId")
                    ? {
                        ability: {
                          definitionId: data.get("rewardAbilityId"),
                          count: Number(data.get("rewardAbilityCount") || 1)
                        }
                      }
                    : {})
                }
              : undefined
        })
      if (kind === "offer")
        await api.post(`/families/${familyId}/offers`, {
          title: data.get("name"),
          description: data.get("description"),
          icon: data.get("icon") || "🎁",
          priceCurrencyId: data.get("currencyId"),
          priceAmount: data.get("amount"),
          abilityDefinitionId: data.get("abilityId")
        })
      if (kind === "scheduled")
        await api.post(`/families/${familyId}/scheduled-grants`, {
          name: data.get("name"),
          schedule: { frequency: data.get("frequency"), interval: 1 },
          timezone: Intl.DateTimeFormat().resolvedOptions().timeZone,
          audience: everyone,
          nextRunAt: new Date(String(data.get("nextRunAt"))).toISOString(),
          currencyId: data.get("currencyId"),
          currencyAmount: data.get("amount")
        })
      showToast("Создано")
      onDone()
    } catch (error) {
      showToast(toApiMessage(error), "danger")
    }
  }
  return (
    <Dialog open={Boolean(kind)} onClose={onClose} title={kind ? titles[kind] : "Создание"}>
      <form className="form-stack" onSubmit={submit}>
        <Field label="Название">
          <input name="name" required />
        </Field>
        {kind !== "scheduled" ? (
          <>
            <Field label="Эмодзи">
              <input name="icon" defaultValue={kind === "achievement" ? "🏆" : kind === "offer" ? "🎁" : "✨"} />
            </Field>
            <Field label="Описание">
              <textarea name="description" rows={2} />
            </Field>
          </>
        ) : null}
        {kind === "ability" || kind === "achievement" ? (
          <Field label="Редкость">
            <select name="rarity">
              <option value="COMMON">Обычная</option>
              <option value="RARE">Редкая</option>
              <option value="EPIC">Эпическая</option>
              <option value="LEGENDARY">Легендарная</option>
            </select>
          </Field>
        ) : null}
        {kind === "achievement" ? (
          <>
            <div className="form-grid">
              <Field label="Бонус валютой">
                <select name="rewardCurrencyId">
                  <option value="">Без валюты</option>
                  {collection(currencies.data).map((currency) => (
                    <option key={currency.id} value={currency.id}>
                      {currency.icon} {currency.name}
                    </option>
                  ))}
                </select>
              </Field>
              <Field label="Количество">
                <input name="rewardCurrencyAmount" inputMode="numeric" />
              </Field>
            </div>
            <div className="form-grid">
              <Field label="Бонус способностью">
                <select name="rewardAbilityId">
                  <option value="">Без способности</option>
                  {collection(abilities.data).map((ability) => (
                    <option key={ability.id} value={ability.id}>
                      {ability.icon} {ability.name}
                    </option>
                  ))}
                </select>
              </Field>
              <Field label="Количество">
                <input name="rewardAbilityCount" type="number" min="1" max="100" defaultValue="1" />
              </Field>
            </div>
          </>
        ) : null}
        {kind === "ability" ? (
          <>
            <AudienceSelector label="Кому можно выдавать" value={holderAudience} onChange={setHolderAudience} />
            <Field label="Эффект">
              <select name="effect">
                <option value="MANUAL">Ручной</option>
                <option value="SKIP_TASK">Пропустить свою задачу</option>
              </select>
            </Field>
            <label className="check-row">
              <input name="targetRequired" type="checkbox" />
              <span>При использовании нужно выбрать человека</span>
            </label>
            <AudienceSelector label="На кого можно использовать" value={targetAudience} onChange={setTargetAudience} />
            <label className="check-row">
              <input name="requireTargetApproval" type="checkbox" />
              <span>Нужно подтверждение выбранного человека</span>
            </label>
            <AudienceSelector label="Кто ещё подтверждает" value={approverAudience} onChange={setApproverAudience} />
            <Field label="Сколько подтверждений нужно">
              <select name="approverMode">
                <option value="ANY_ONE">Одного достаточно</option>
                <option value="ALL">Нужны все</option>
              </select>
            </Field>
          </>
        ) : null}
        {kind === "offer" ? (
          <>
            <Field label="Что выдаём">
              <select name="abilityId" required>
                <option value="">Выберите способность</option>
                {collection(abilities.data).map((item) => (
                  <option key={item.id} value={item.id}>
                    {item.name}
                  </option>
                ))}
              </select>
            </Field>
            <Field label="Цена">
              <input name="amount" required inputMode="numeric" />
            </Field>
          </>
        ) : null}
        {kind === "offer" || kind === "scheduled" ? (
          <Field label={kind === "offer" ? "Валюта цены" : "Что выдаём"}>
            <select name="currencyId" required>
              <option value="">Выберите валюту</option>
              {collection(currencies.data).map((item) => (
                <option key={item.id} value={item.id}>
                  {item.icon} {item.name}
                </option>
              ))}
            </select>
          </Field>
        ) : null}
        {kind === "scheduled" ? (
          <>
            <Field label="Количество">
              <input name="amount" required inputMode="numeric" />
            </Field>
            <Field label="Повтор">
              <select name="frequency">
                <option value="DAILY">Каждый день</option>
                <option value="WEEKLY">Каждую неделю</option>
                <option value="MONTHLY">Каждый месяц</option>
              </select>
            </Field>
            <Field label="Первая выдача">
              <input name="nextRunAt" type="datetime-local" required />
            </Field>
          </>
        ) : null}
        <div className="dialog__actions">
          <Button type="button" variant="ghost" onClick={onClose}>
            Отмена
          </Button>
          <Button type="submit">Создать</Button>
        </div>
      </form>
    </Dialog>
  )
}

// eslint-disable-next-line @typescript-eslint/no-unused-vars
function LegacyCurrencyCreateDialog({ open, onClose, onDone }: { open: boolean; onClose(): void; onDone(): void }) {
  const { familyId } = useFamilyId()
  const groups = useGroups()
  const all = groups.find((g) => g.isSystem)
  const everyone = { memberIds: [] as string[], groupIds: all ? [all.id] : [] }
  const [issueAudience, setIssueAudience] = useState<Audience>(everyone)
  const [receiveAudience, setReceiveAudience] = useState<Audience>(everyone)
  const [deductAudience, setDeductAudience] = useState<Audience>(everyone)
  const [providerAudience, setProviderAudience] = useState<Audience>(everyone)
  async function submit(e: FormEvent<HTMLFormElement>) {
    e.preventDefault()
    const data = new FormData(e.currentTarget)
    const icon = String(data.get("icon") || "🪙")
    try {
      await api.post(`/families/${familyId}/currencies`, {
        name: data.get("name"),
        code: `C${Date.now().toString(36)}`.toUpperCase().slice(0, 12),
        symbol: icon,
        icon,
        color: data.get("color") || "#6D5EF7",
        description: data.get("description") || "",
        issueAudience,
        receiveAudience,
        deductAudience,
        redeemProviderAudience: providerAudience,
        redeemProviderApprovalRequired: data.get("approval") === "on",
        transferEnabled: data.get("transfer") === "on",
        balanceVisibility: data.get("balanceVisibility")
      })
      showToast("Валюта создана")
      onDone()
    } catch (error) {
      showToast(toApiMessage(error), "danger")
    }
  }
  return (
    <Dialog open={open} onClose={onClose} title="Новая валюта" size="lg">
      <form className="form-stack" onSubmit={submit}>
        <div className="form-grid">
          <Field label="Название">
            <input name="name" required />
          </Field>
          <Field label="Эмодзи">
            <input name="icon" defaultValue="🪙" maxLength={8} />
          </Field>
        </div>
        <Field label="Описание">
          <textarea name="description" rows={2} />
        </Field>
        <div className="advanced-box">
          <strong>Права валюты по группам и людям</strong>
          <p className="muted">Выберите группы целиком или отдельных участников для каждого действия.</p>
        </div>
        <AudienceSelector label="Кто может начислять" value={issueAudience} onChange={setIssueAudience} />
        <AudienceSelector label="Кто может получать и тратить" value={receiveAudience} onChange={setReceiveAudience} />
        <AudienceSelector label="Кто может назначать штрафы" value={deductAudience} onChange={setDeductAudience} />
        <AudienceSelector label="Кто принимает оплату" value={providerAudience} onChange={setProviderAudience} />
        <label className="check-row">
          <input name="transfer" type="checkbox" defaultChecked />
          <span>Можно передавать</span>
        </label>
        <Field label="Кто видит балансы">
          <select name="balanceVisibility" defaultValue="FAMILY">
            <option value="FAMILY">Вся семья</option>
            <option value="RESTRICTED">Только владелец и участники правил</option>
          </select>
        </Field>
        <label className="check-row">
          <input name="approval" type="checkbox" />
          <span>Трата требует подтверждения выбранного человека</span>
        </label>
        <input name="color" type="hidden" value="#6D5EF7" />
        <div className="dialog__actions">
          <Button type="button" variant="ghost" onClick={onClose}>
            Отмена
          </Button>
          <Button type="submit">Создать</Button>
        </div>
      </form>
    </Dialog>
  )
}

export function EventsV1Page() {
  const { familyId } = useFamilyId()
  const client = useQueryClient()
  const [review, setReview] = useState<Actions["approvals"][number] | null>(null)
  useEffect(() => {
    void Promise.all([
      api.post(`/families/${familyId}/notifications/read-all`),
      api.post("/notifications/read-all")
    ]).then(() => client.invalidateQueries({ queryKey: ["notifications", "badge"] }))
  }, [client, familyId])
  const activity = useQuery({
    queryKey: ["v1", familyId, "activity"],
    queryFn: () => api.get<{ items: Activity[] }>(`/families/${familyId}/activity`)
  })
  const notifications = useQuery({
    queryKey: ["notifications", "inbox"],
    queryFn: () => api.get<Paged<AppNotification>>("/notifications?limit=50")
  })
  const actions = useQuery({
    queryKey: ["v1", familyId, "actions"],
    queryFn: () => api.get<Actions>(`/families/${familyId}/activity/actions`)
  })
  const decide = useMutation({
    mutationFn: async (input: { type: "task" | "ability" | "redeem"; id: string; approve: boolean; reason?: string }) =>
      api.post(
        `/families/${familyId}/${input.type === "task" ? `tasks/completions/${input.id}/decision` : input.type === "ability" ? `ability-uses/${input.id}/decision` : `currencies/redeems/${input.id}/decision`}`,
        { approve: input.approve, reason: input.reason }
      ),
    onSuccess: () => {
      showToast("Решение сохранено")
      setReview(null)
      void client.invalidateQueries({ queryKey: ["v1", familyId] })
    },
    onError: (e) => showToast(toApiMessage(e), "danger")
  })
  const actionData = actions.data
  return (
    <div className="page-stack events-page">
      <PageHeader title="События" />
      {notifications.data?.items.length ? (
        <section className="panel notification-inbox">
          <h2>Уведомления</h2>
          {notifications.data.items.map((item) => (
            <FamilyNotificationItem
              key={item.id}
              icon={item.type.includes("task") ? "✅" : item.type.includes("currency") ? "🪙" : "✨"}
              title={item.title}
              body={item.body ?? undefined}
              time={new Date(item.createdAt).toLocaleString("ru-RU")}
              unread={!item.readAt}
            />
          ))}
        </section>
      ) : null}
      {actionData?.approvals.length || actionData?.redeems.length ? (
        <section className="action-inbox">
          <h2>Требует действия</h2>
          {actionData.approvals.map((item) => {
            const completion = item.completion
            const ability = item.abilityUse
            return (
              <FamilyApprovalCard
                key={item.id}
                icon={completion ? "✅" : "✨"}
                title={
                  completion
                    ? `${completion.submitter.displayName} завершил(а) «${completion.assignment.occurrence.definition.title}»`
                    : `${ability?.actor.displayName} использует «${ability?.instance.definition.name}»`
                }
                description="Подтвердите или отклоните действие"
                actions={
                  <div className="button-row">
                    {completion ? (
                      <Button size="sm" onClick={() => setReview(item)}>
                        Посмотреть результат
                      </Button>
                    ) : (
                      <>
                        <Button
                          size="sm"
                          variant="ghost"
                          onClick={() => decide.mutate({ type: "ability", id: ability!.id, approve: false })}
                        >
                          Отклонить
                        </Button>
                        <Button
                          size="sm"
                          onClick={() => decide.mutate({ type: "ability", id: ability!.id, approve: true })}
                        >
                          Подтвердить
                        </Button>
                      </>
                    )}
                  </div>
                }
              />
            )
          })}
          {actionData.redeems.map((item) => (
            <FamilyApprovalCard
              key={item.id}
              icon={item.currency.icon}
              title={`Запрос на трату ${item.amount} ${item.currency.symbol}`}
              description={item.comment || "Без комментария"}
              actions={
                <div className="button-row">
                  <Button
                    size="sm"
                    variant="ghost"
                    onClick={() => decide.mutate({ type: "redeem", id: item.id, approve: false })}
                  >
                    Отклонить
                  </Button>
                  <Button size="sm" onClick={() => decide.mutate({ type: "redeem", id: item.id, approve: true })}>
                    Подтвердить
                  </Button>
                </div>
              }
            />
          ))}
        </section>
      ) : null}
      <section className="panel">
        <h2>История семьи</h2>
        {activity.isLoading ? (
          <LoadingState />
        ) : (
          collection(activity.data).map((item) => <ActivityRow key={item.id} item={item} />)
        )}
      </section>
      <TaskReviewDialog
        key={review?.id ?? "closed"}
        completion={review?.completion ?? null}
        title={review?.completion?.assignment.occurrence.definition.title ?? "Результат задачи"}
        loading={decide.isPending}
        onClose={() => setReview(null)}
        onDecision={(approve, reason) => {
          const completion = review?.completion
          if (completion) decide.mutate({ type: "task", id: completion.id, approve, reason })
        }}
      />
    </div>
  )
}

function TaskReviewDialog({
  completion,
  title,
  loading,
  onClose,
  onDecision
}: {
  completion: {
    id: string
    comment: string
    media: Array<{ url: string; type: "IMAGE" | "VIDEO" }>
    submitter?: FamilyMember
  } | null
  title: string
  loading: boolean
  onClose(): void
  onDecision(approve: boolean, reason: string): void
}) {
  const [reason, setReason] = useState("")
  return (
    <Dialog
      open={Boolean(completion)}
      onClose={onClose}
      title={completion ? `Результат: ${title}` : "Результат задачи"}
      size="lg"
    >
      {completion ? (
        <div className="form-stack">
          <div>
            <small>Исполнитель</small>
            <h3>{completion.submitter?.displayName ?? "Исполнитель"}</h3>
          </div>
          <div>
            <small>Комментарий</small>
            <p>{completion.comment || "Без комментария"}</p>
          </div>
          <TaskMediaGallery media={completion.media} emptyMessage="Вложения не добавлены" />
          <Field label="Комментарий к решению" hint="При возврате объясните, что нужно исправить">
            <textarea value={reason} onChange={(event) => setReason(event.target.value)} rows={3} maxLength={500} />
          </Field>
          <div className="dialog__actions">
            <Button
              type="button"
              variant="danger"
              disabled={!reason.trim()}
              loading={loading}
              onClick={() => onDecision(false, reason.trim())}
            >
              Вернуть на доработку
            </Button>
            <Button type="button" loading={loading} onClick={() => onDecision(true, reason.trim())}>
              Принять и выдать награду
            </Button>
          </div>
        </div>
      ) : null}
    </Dialog>
  )
}

function ActivityRow({ item }: { item: Activity }) {
  return (
    <FamilyActivityItem
      icon={
        item.type.includes("task")
          ? "✅"
          : item.type.includes("currency")
            ? "🪙"
            : item.type.includes("achievement")
              ? "🏆"
              : "✨"
      }
      title={item.title}
      body={item.body}
      time={new Date(item.createdAt).toLocaleString("ru-RU", {
        day: "numeric",
        month: "short",
        hour: "2-digit",
        minute: "2-digit"
      })}
    />
  )
}

function AudienceSelector({
  label,
  value,
  onChange,
  membersOnly = false
}: {
  label: string
  value: Audience
  onChange(value: Audience): void
  membersOnly?: boolean
}) {
  const { familyId } = useFamilyId()
  const client = useQueryClient()
  const members = useMembers()
  const groups = useGroups()
  async function createGroup() {
    const name = window.prompt("Название новой группы")?.trim()
    if (!name) return
    try {
      const group = await api.post<Group>(`/families/${familyId}/groups`, { name, memberIds: value.memberIds })
      await client.invalidateQueries({ queryKey: ["v1", familyId, "groups"] })
      onChange({ memberIds: [], groupIds: [...new Set([...value.groupIds, group.id])] })
      showToast(`Группа «${name}» создана и выбрана`)
    } catch (error) {
      showToast(toApiMessage(error), "danger")
    }
  }
  return (
    <FamilyAudienceSelector
      label={label}
      value={value}
      onChange={onChange}
      members={members.map((member) => ({ id: member.id, displayName: member.displayName }))}
      groups={groups.map((group) => ({
        id: group.id,
        name: group.name,
        isSystem: group.isSystem,
        memberIds: group.memberships.map((membership) => membership.memberId)
      }))}
      membersOnly={membersOnly}
      onCreateGroup={!membersOnly && value.memberIds.length ? () => void createGroup() : undefined}
    />
  )
}
function useMembers() {
  const { familyId } = useFamilyId()
  const query = useQuery({
    queryKey: ["members", familyId],
    queryFn: () => api.get<{ items: FamilyMember[] }>(`/families/${familyId}/members`)
  })
  return collection(query.data).filter((member) => member.status === "ACTIVE")
}
function useGroups() {
  const { familyId } = useFamilyId()
  const query = useQuery({
    queryKey: ["v1", familyId, "groups"],
    queryFn: () => api.get<{ items: Group[] }>(`/families/${familyId}/groups`)
  })
  return collection(query.data)
}
